import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Table,
  Badge,
  Alert,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import Content from "../../../Components/Dashboard/Content/Content";
import DeltaExchangeService from "../../../Service/delta-exchange.service";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { createChart } from "lightweight-charts";
import {
  BarChart3,
  TrendingUp,
  Calculator,
  Play,
  PlayCircle,
  RotateCcw,
  Download,
  Settings,
  Target,
  DollarSign,
  Shield,
  Activity,
  Calendar,
  Clock,
  Zap,
} from "lucide-react";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Backtesting = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("BTCUSD");
  const [timeframe, setTimeframe] = useState("1h");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialCapital, setInitialCapital] = useState(10000);
  const [riskPerTrade, setRiskPerTrade] = useState(2);
  const [showSettings, setShowSettings] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [chartData, setChartData] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [showCandlestickChart, setShowCandlestickChart] = useState(false);
  const [isBacktestRunning, setIsBacktestRunning] = useState(false);
  const [currentBacktestIndex, setCurrentBacktestIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(20); // ms delay per candle

  // Chart refs
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const buyMarkersRef = useRef([]);
  const sellMarkersRef = useRef([]);
  const [advancedSettings, setAdvancedSettings] = useState({
    // Common settings
    stopLoss: 2,
    takeProfit: 4,
    maxTrades: 1000,
    commission: 0.1,
    slippage: 0.05,
    positionSize: "fixed",
    leverage: 1,
    allowShortSelling: true,
    allowLongTrading: true,
    strategyType: "both", // 'long', 'short', 'both'

    // Strategy-specific parameters
    ma_crossover: {
      fastEMA: 50,
      slowEMA: 200,
      trendFilter: true,
    },
    rsi_ma_confluence: {
      rsiPeriod: 14,
      emaPeriod: 20,
      oversoldLevel: 30,
      overboughtLevel: 70,
    },
    breakout_strategy: {
      lookbackPeriod: 20,
      breakoutThreshold: 0.5,
      sessionType: "london", // 'london', 'asian', 'both'
      volumeConfirmation: true,
    },
    price_action: {
      pinBarThreshold: 0.6,
      engulfingSize: 0.3,
      retestTolerance: 0.2,
      lookbackCandles: 20,
    },
    macd_reversal: {
      fastPeriod: 12,
      slowPeriod: 26,
      signalPeriod: 9,
      histogramThreshold: 0,
    }
  });

  const [strategyBuilder, setStrategyBuilder] = useState({
    conditions: [
      {
        id: 1,
        type: "indicator",
        indicator: "sma",
        period: 20,
        operator: ">",
        value: 0,
        condition: "price",
      },
    ],
    actions: [
      {
        id: 1,
        condition: "all",
        signal: "BUY",
        description: "Buy when all conditions are met",
      },
    ],
  });

  const strategies = [
    {
      id: "ma_crossover",
      name: "Moving Average Crossover Strategy",
      description: "Trend-following strategy using 50 EMA + 200 EMA cross",
      documentation: {
        overview:
          "This strategy identifies and follows market trends using Exponential Moving Average (EMA) crossovers. Buy when 50 EMA crosses above 200 EMA, sell when it crosses below. MT4 indicators: EMA, SMA",
        parameters: {
          fastEMA: "Fast EMA period (default: 50)",
          slowEMA: "Slow EMA period (default: 200)",
          trendFilter: "Use trend filter (default: true)",
        },
        signals: {
          buy: "When 50 EMA crosses above 200 EMA",
          sell: "When 50 EMA crosses below 200 EMA",
        },
        bestFor: "Trending markets, medium to long-term timeframes",
        riskLevel: "Medium",
      },
    },
    {
      id: "rsi_ma_confluence",
      name: "RSI + MA Confluence Strategy",
      description: "Overbought/oversold zone + trend confirmation using RSI (14) + 20 EMA",
      documentation: {
        overview:
          "This strategy combines RSI for overbought/oversold conditions with EMA for trend confirmation. Buy when RSI < 30 (oversold) AND price is above 20 EMA. MT4 indicators: RSI, EMA",
        parameters: {
          rsiPeriod: "RSI calculation period (default: 14)",
          emaPeriod: "EMA period for trend confirmation (default: 20)",
          oversoldLevel: "RSI oversold level (default: 30)",
          overboughtLevel: "RSI overbought level (default: 70)",
        },
        signals: {
          buy: "When RSI < 30 (oversold) AND price > 20 EMA (uptrend)",
          sell: "When RSI > 70 (overbought) AND price < 20 EMA (downtrend)",
        },
        bestFor: "Trending markets with pullbacks, medium timeframes",
        riskLevel: "Medium",
      },
    },
    {
      id: "breakout_strategy",
      name: "Breakout Strategy (London/Asian Breakout)",
      description: "High volatility breakout during London/NY session",
      documentation: {
        overview:
          "This strategy identifies breakouts from support/resistance levels during high volatility sessions (London/NY). MT4 indicators: Support/Resistance, Boxes",
        parameters: {
          lookbackPeriod: "Period for calculating support/resistance (default: 20)",
          breakoutThreshold: "Minimum breakout percentage (default: 0.5%)",
          sessionType: "Session type: 'london', 'asian', 'both' (default: 'london')",
          volumeConfirmation: "Require volume confirmation (default: true)",
        },
        signals: {
          buy: "When price breaks above resistance during London/NY session",
          sell: "When price breaks below support during London/NY session",
        },
        bestFor: "High volatility markets, intraday timeframes",
        riskLevel: "High",
      },
    },
    {
      id: "price_action",
      name: "Price Action Strategy",
      description: "Candlestick patterns: Pin Bar, Engulfing, Break & Retest",
      documentation: {
        overview:
          "Pure chart structure trading using candlestick patterns. No indicators required. Identifies Pin Bars, Engulfing patterns, and Break & Retest setups.",
        parameters: {
          pinBarThreshold: "Pin bar tail ratio (default: 0.6)",
          engulfingSize: "Minimum engulfing size (default: 0.3%)",
          retestTolerance: "Retest tolerance percentage (default: 0.2%)",
          lookbackCandles: "Lookback period for structure (default: 20)",
        },
        signals: {
          buy: "Bullish Pin Bar, Bullish Engulfing, or Break & Retest above support",
          sell: "Bearish Pin Bar, Bearish Engulfing, or Break & Retest below resistance",
        },
        bestFor: "All market conditions, any timeframe",
        riskLevel: "Medium",
      },
    },
    {
      id: "macd_reversal",
      name: "MACD Trend Reversal Strategy",
      description: "MACD histogram + signal line cross for trend reversal confirmations",
      documentation: {
        overview:
          "This strategy uses MACD histogram and signal line crossovers to identify trend reversals. Buy on bullish MACD cross with positive histogram. MT4 indicator: MACD",
        parameters: {
          fastPeriod: "Fast EMA period (default: 12)",
          slowPeriod: "Slow EMA period (default: 26)",
          signalPeriod: "Signal line period (default: 9)",
          histogramThreshold: "Histogram threshold for confirmation (default: 0)",
        },
        signals: {
          buy: "When MACD crosses above signal line AND histogram is positive",
          sell: "When MACD crosses below signal line AND histogram is negative",
        },
        bestFor: "Trend reversal markets, medium timeframes",
        riskLevel: "Medium",
      },
    },
  ];

  // Helper function to calculate EMA (Exponential Moving Average)
  const calculateEMA = (prices, index, period) => {
    if (index < period - 1) return null;
    
    // Initialize EMA with SMA for the first period
    let sum = 0;
    for (let i = index - period + 1; i <= index; i++) {
      sum += prices[i];
    }
    let ema = sum / period;
    
    // Calculate EMA using the multiplier
    const multiplier = 2 / (period + 1);
    
    // Continue from the next index if we have more data
    for (let i = index - period + 2; i <= index; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }
    
    return ema;
  };

  // Helper function to calculate RSI (Relative Strength Index) using Wilder's smoothing
  const calculateRSI = (prices, index, period) => {
    if (index < period) return null;
    
    let avgGain = 0;
    let avgLoss = 0;
    
    // Calculate initial average gain and loss
    for (let i = index - period + 1; i <= index; i++) {
      const change = prices[i] - prices[i - 1];
      if (change > 0) {
        avgGain += change;
      } else {
        avgLoss += Math.abs(change);
      }
    }
    
    avgGain = avgGain / period;
    avgLoss = avgLoss / period;
    
    // Use Wilder's smoothing for subsequent calculations
    if (index > period) {
      const currentChange = prices[index] - prices[index - 1];
      const currentGain = currentChange > 0 ? currentChange : 0;
      const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;
      
      avgGain = (avgGain * (period - 1) + currentGain) / period;
      avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
    }
    
    if (avgLoss === 0) return 100;
    
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  };

  // Render strategy-specific parameters
  const renderStrategyParameters = (strategyId) => {
    const strategy = strategies.find((s) => s.id === strategyId);
    if (!strategy || !strategy.documentation?.parameters) return null;

    const params = strategy.documentation.parameters;
    const paramKeys = Object.keys(params);

    // Get current strategy settings or initialize with defaults
    const currentSettings = advancedSettings[strategyId] || {};

    return (
      <div className="strategy-parameters mt-3">
        <h6 className=" mb-3">
          <Settings size={16} className="me-2" />
          {strategy.name} Parameters
        </h6>
        <Alert variant="info" className="mb-3">
          <small>
            <strong>Tip:</strong> Adjust these parameters to optimize your
            strategy performance. Changes are automatically saved and will be
            used in the next backtest.
          </small>
        </Alert>
        <Row>
          {paramKeys.map((paramKey) => {
            const isDecimal =
              paramKey.includes("Threshold") ||
              paramKey.includes("Ratio") ||
              paramKey.includes("Spacing") ||
              paramKey.includes("Strength") ||
              paramKey.includes("Target") ||
              paramKey.includes("StdDev");
            const isBoolean =
              paramKey.includes("Filter") || paramKey.includes("Confirmation");
            const isPercentage =
              paramKey.includes("Threshold") ||
              paramKey.includes("Target") ||
              paramKey.includes("Spacing") ||
              paramKey.includes("Ratio");

            return (
              <Col md={6} key={paramKey}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold d-flex align-items-center">
                    <span className="me-2">
                      {paramKey.charAt(0).toUpperCase() +
                        paramKey.slice(1).replace(/([A-Z])/g, " $1")}
                    </span>
                    {isPercentage && (
                      <Badge bg="secondary" className="small">
                        %
                      </Badge>
                    )}
                  </Form.Label>

                  {isBoolean ? (
                    <Form.Check
                      type="switch"
                      id={`${strategyId}-${paramKey}`}
                      label={currentSettings[paramKey] ? "Enabled" : "Disabled"}
                      checked={currentSettings[paramKey] || false}
                      onChange={(e) => {
                        setAdvancedSettings((prev) => ({
                          ...prev,
                          [strategyId]: {
                            ...prev[strategyId],
                            [paramKey]: e.target.checked,
                          },
                        }));
                      }}
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="number"
                        id={`${strategyId}-${paramKey}`}
                        value={currentSettings[paramKey] || ""}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === "") {
                            // Allow empty input for clearing
                            setAdvancedSettings((prev) => ({
                              ...prev,
                              [strategyId]: {
                                ...prev[strategyId],
                                [paramKey]: "",
                              },
                            }));
                            return;
                          }

                          const value = isDecimal
                            ? parseFloat(inputValue)
                            : parseInt(inputValue);
                          if (!isNaN(value)) {
                            setAdvancedSettings((prev) => ({
                              ...prev,
                              [strategyId]: {
                                ...prev[strategyId],
                                [paramKey]: value,
                              },
                            }));
                          }
                        }}
                        step={isDecimal ? 0.01 : 1}
                        min={isDecimal ? 0 : 1}
                        max={
                          paramKey.includes("Period")
                            ? 200
                            : paramKey.includes("Level")
                              ? 100
                              : undefined
                        }
                        className="flex-grow-1"
                        placeholder={`Enter ${paramKey}`}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          // Reset to default value
                          const defaultValue = getDefaultValue(
                            strategyId,
                            paramKey
                          );
                          setAdvancedSettings((prev) => ({
                            ...prev,
                            [strategyId]: {
                              ...prev[strategyId],
                              [paramKey]: defaultValue,
                            },
                          }));
                        }}
                        title="Reset to default"
                      >
                        <i className="fas fa-undo"></i>
                      </Button>
                    </div>
                  )}

                  <Form.Text className="text-muted small">
                    {params[paramKey]}
                    {currentSettings[paramKey] !== undefined && (
                      <span className="ms-2 text-success">
                        <i className="fas fa-check-circle"></i> Current:{" "}
                        {currentSettings[paramKey]}
                      </span>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>
            );
          })}
        </Row>

        {/* Parameter Validation */}
        {(() => {
          const validation = validateStrategyParameters(strategyId);
          return (
            <>
              {validation.errors.length > 0 && (
                <Alert variant="danger" className="mt-3 validation-alert">
                  <h6 className="mb-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Parameter Errors
                  </h6>
                  <ul className="mb-0">
                    {validation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              {validation.warnings.length > 0 && (
                <Alert variant="warning" className="mt-3 validation-alert">
                  <h6 className="mb-2">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    Parameter Warnings
                  </h6>
                  <ul className="mb-0">
                    {validation.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          );
        })()}

        {/* Parameter Summary */}
        <div className="mt-3 p-3 parameter-summary">
          <h6 className=" mb-2">
            <i className="fas fa-info-circle me-2"></i>
            Current Parameter Values
          </h6>
          <div className="row">
            {paramKeys.map((paramKey) => (
              <div key={paramKey} className="col-md-6 mb-1">
                <small className="text-muted">
                  <strong>{paramKey}:</strong>{" "}
                  {currentSettings[paramKey] !== undefined
                    ? currentSettings[paramKey]
                    : "Default"}
                </small>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  // Reset all parameters to defaults
                  const defaults = {};
                  paramKeys.forEach((paramKey) => {
                    defaults[paramKey] = getDefaultValue(strategyId, paramKey);
                  });
                  setAdvancedSettings((prev) => ({
                    ...prev,
                    [strategyId]: defaults,
                  }));
                }}
              >
                <i className="fas fa-undo me-1"></i>
                Reset All to Defaults
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  const validation = validateStrategyParameters(strategyId);
                  if (validation.errors.length === 0) {
                    alert("✅ All parameters are valid!");
                  } else {
                    alert(
                      `❌ Please fix ${validation.errors.length} error(s) before running backtest.`
                    );
                  }
                }}
              >
                <i className="fas fa-check me-1"></i>
                Validate Parameters
              </Button>

              <Button
                variant="outline-info"
                size="sm"
                onClick={() => {
                  const presetName = prompt("Enter preset name:");
                  if (presetName && presetName.trim()) {
                    saveParameterPreset(strategyId, presetName.trim());
                  }
                }}
              >
                <i className="fas fa-save me-1"></i>
                Save Preset
              </Button>

              {getAvailablePresets(strategyId).length > 0 && (
                <div className="dropdown d-inline-block preset-dropdown">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="fas fa-folder-open me-1"></i>
                    Load Preset
                  </Button>
                  <ul className="dropdown-menu">
                    {getAvailablePresets(strategyId).map((presetName) => (
                      <li key={presetName}>
                        <button
                          className="dropdown-item"
                          onClick={() =>
                            loadParameterPreset(strategyId, presetName)
                          }
                        >
                          {presetName}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Preset Info */}
            {getAvailablePresets(strategyId).length > 0 && (
              <div className="mt-2">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  {getAvailablePresets(strategyId).length} preset(s) available
                  for this strategy
                </small>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get default values for parameters
  const getDefaultValue = (strategyId, paramKey) => {
    const defaults = {
      trendFollowing: {
        shortMA: 10,
        longMA: 20,
        trendStrength: 0.5,
        volumeFilter: true,
      },
      meanReversion: {
        lookbackPeriod: 20,
        oversoldThreshold: 30,
        overboughtThreshold: 70,
        rsiPeriod: 14,
        bbPeriod: 20,
        bbStdDev: 2,
      },
      breakout: {
        lookbackPeriod: 20,
        breakoutThreshold: 0.02,
        volumeConfirmation: true,
        minVolumeRatio: 1.5,
      },
      scalping: {
        shortMA: 5,
        longMA: 10,
        profitTarget: 0.5,
        stopLoss: 0.3,
        maxHoldTime: 30,
      },
      momentum: {
        momentumPeriod: 10,
        momentumThreshold: 0.02,
        volumePeriod: 14,
        volumeThreshold: 1.2,
      },
      rsiStrategy: {
        rsiPeriod: 14,
        oversoldLevel: 30,
        overboughtLevel: 70,
        exitLevel: 50,
      },
      bollingerBands: {
        bbPeriod: 20,
        bbStdDev: 2,
        squeezeThreshold: 0.1,
        breakoutThreshold: 0.02,
      },
      macdStrategy: {
        fastPeriod: 12,
        slowPeriod: 26,
        signalPeriod: 9,
        histogramThreshold: 0,
      },
      gridTrading: {
        gridLevels: 10,
        gridSpacing: 0.5,
        maxPositions: 5,
        rebalanceThreshold: 0.1,
      },
      dcaStrategy: {
        dcaAmount: 100,
        dcaInterval: 24,
        maxDcaCount: 10,
        profitTarget: 5,
      },
    };

    return defaults[strategyId]?.[paramKey] || 0;
  };

  // Validation function for strategy parameters
  const validateStrategyParameters = (strategyId) => {
    const currentSettings = advancedSettings[strategyId] || {};
    const errors = [];
    const warnings = [];

    switch (strategyId) {
      case "ma_crossover":
        if (
          currentSettings.fastEMA &&
          currentSettings.slowEMA &&
          currentSettings.fastEMA >= currentSettings.slowEMA
        ) {
          errors.push("Fast EMA period must be less than Slow EMA period");
        }
        break;

      case "rsi_ma_confluence":
        if (
          currentSettings.oversoldLevel &&
          currentSettings.overboughtLevel &&
          currentSettings.oversoldLevel >= currentSettings.overboughtLevel
        ) {
          errors.push(
            "Oversold level must be less than overbought level"
          );
        }
        if (
          currentSettings.oversoldLevel &&
          (currentSettings.oversoldLevel < 0 ||
            currentSettings.oversoldLevel > 50)
        ) {
          warnings.push("Oversold level should be between 0 and 50");
        }
        if (
          currentSettings.overboughtLevel &&
          (currentSettings.overboughtLevel < 50 ||
            currentSettings.overboughtLevel > 100)
        ) {
          warnings.push("Overbought level should be between 50 and 100");
        }
        break;

      case "breakout_strategy":
        if (
          currentSettings.breakoutThreshold &&
          (currentSettings.breakoutThreshold < 0 ||
            currentSettings.breakoutThreshold > 5)
        ) {
          warnings.push("Breakout threshold should be between 0 and 5%");
        }
        break;

      case "price_action":
        if (
          currentSettings.pinBarThreshold &&
          (currentSettings.pinBarThreshold < 0 ||
            currentSettings.pinBarThreshold > 1)
        ) {
          warnings.push("Pin bar threshold should be between 0 and 1");
        }
        break;

      case "macd_reversal":
        if (
          currentSettings.fastPeriod &&
          currentSettings.slowPeriod &&
          currentSettings.fastPeriod >= currentSettings.slowPeriod
        ) {
          errors.push("Fast period must be less than slow period");
        }
        break;
    }

    return { errors, warnings };
  };

  // Save parameter preset
  const saveParameterPreset = (strategyId, presetName) => {
    const currentSettings = advancedSettings[strategyId] || {};
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");

    if (!presets[strategyId]) {
      presets[strategyId] = {};
    }

    presets[strategyId][presetName] = {
      ...currentSettings,
      savedAt: new Date().toISOString(),
      name: presetName,
    };

    localStorage.setItem("strategyPresets", JSON.stringify(presets));
    alert(`✅ Preset "${presetName}" saved successfully!`);
  };

  // Load parameter preset
  const loadParameterPreset = (strategyId, presetName) => {
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");
    const preset = presets[strategyId]?.[presetName];

    if (preset) {
      setAdvancedSettings((prev) => ({
        ...prev,
        [strategyId]: preset,
      }));
      alert(`✅ Preset "${presetName}" loaded successfully!`);
    } else {
      alert("❌ Preset not found!");
    }
  };

  // Get available presets for a strategy
  const getAvailablePresets = (strategyId) => {
    const presets = JSON.parse(localStorage.getItem("strategyPresets") || "{}");
    return presets[strategyId] ? Object.keys(presets[strategyId]) : [];
  };

  // Generate strategy signals for test animation
  const generateStrategySignals = (candlestickData) => {
    if (!selectedStrategy || !candlestickData.length) return [];

    const signals = [];
    const prices = candlestickData.map((candle) => candle.close);
    const volumes = candlestickData.map((candle) => candle.volume || 1000); // Default volume if not available
    const strategyParams = advancedSettings[selectedStrategy] || {};

    // Get minimum lookback period based on strategy
    const minLookback = Math.max(
      strategyParams.fastEMA || 50,
      strategyParams.slowEMA || 200,
      strategyParams.rsiPeriod || 14,
      strategyParams.emaPeriod || 20,
      strategyParams.lookbackPeriod || 20,
      strategyParams.lookbackCandles || 20,
      strategyParams.slowPeriod || 26,
      strategyParams.signalPeriod || 9
    );

    // Helper function to calculate EMA (corrected calculation)
    const calculateEMALocal = (prices, index, period) => {
      if (index < period - 1) return null;
      
      // Initialize EMA with SMA for the first period
      let sum = 0;
      for (let i = index - period + 1; i <= index; i++) {
        sum += prices[i];
      }
      let ema = sum / period;
      
      // Calculate EMA using the multiplier
      const multiplier = 2 / (period + 1);
      
      // Continue from the next index if we have more data
      for (let i = index - period + 2; i <= index; i++) {
        ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
      }
      
      return ema;
    };

    // Helper function to calculate RSI (using Wilder's smoothing)
    const calculateRSILocal = (prices, index, period) => {
      if (index < period) return null;
      
      let avgGain = 0;
      let avgLoss = 0;
      
      // Calculate initial average gain and loss
      for (let i = index - period + 1; i <= index; i++) {
        const change = prices[i] - prices[i - 1];
        if (change > 0) {
          avgGain += change;
        } else {
          avgLoss += Math.abs(change);
        }
      }
      
      avgGain = avgGain / period;
      avgLoss = avgLoss / period;
      
      // Use Wilder's smoothing for subsequent calculations
      if (index > period) {
        const currentChange = prices[index] - prices[index - 1];
        const currentGain = currentChange > 0 ? currentChange : 0;
        const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;
        
        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
      }
      
      if (avgLoss === 0) return 100;
      
      const rs = avgGain / avgLoss;
      return 100 - (100 / (1 + rs));
    };

    // Strategy signal generation functions for the 5 new strategies

    // 1. Moving Average Crossover Strategy (50 EMA + 200 EMA)
    const getMACrossoverSignal = (index, params) => {
      const fastEMA = params.fastEMA || 50;
      const slowEMA = params.slowEMA || 200;

      if (index < slowEMA) return "HOLD";

      const fastEmaValue = calculateEMALocal(prices, index, fastEMA);
      const slowEmaValue = calculateEMALocal(prices, index, slowEMA);

      if (index < slowEMA + 1) return "HOLD";
      const prevFastEma = calculateEMALocal(prices, index - 1, fastEMA);
      const prevSlowEma = calculateEMALocal(prices, index - 1, slowEMA);

      if (fastEmaValue > slowEmaValue && prevFastEma <= prevSlowEma) {
        return "BUY";
      }
      if (fastEmaValue < slowEmaValue && prevFastEma >= prevSlowEma) {
        return "SELL";
      }
      return "HOLD";
    };

    // 2. RSI + MA Confluence Strategy (RSI 14 + 20 EMA)
    const getRSIMAConfluenceSignal = (index, params) => {
      const rsiPeriod = params.rsiPeriod || 14;
      const emaPeriod = params.emaPeriod || 20;
      const oversoldLevel = params.oversoldLevel || 30;
      const overboughtLevel = params.overboughtLevel || 70;

      if (index < Math.max(rsiPeriod, emaPeriod) + 1) return "HOLD";

      const rsi = calculateRSILocal(prices, index, rsiPeriod);
      const ema = calculateEMALocal(prices, index, emaPeriod);
      const currentPrice = prices[index];

      if (rsi < oversoldLevel && currentPrice > ema) {
        return "BUY";
      }
      if (rsi > overboughtLevel && currentPrice < ema) {
        return "SELL";
      }
      return "HOLD";
    };

    // 3. Breakout Strategy (London/Asian Breakout)
    const getBreakoutStrategySignal = (index, params) => {
      const lookbackPeriod = params.lookbackPeriod || 20;
      const breakoutThreshold = (params.breakoutThreshold || 0.5) / 100;
      const sessionType = params.sessionType || "london";

      if (index < lookbackPeriod) return "HOLD";

      // Get timestamp from candlestick data if available
      const currentTime = candlestickData[index]?.time ? new Date(candlestickData[index].time * 1000) : new Date();
      const hour = currentTime.getUTCHours();

      let inSession = false;
      if (sessionType === "london") {
        inSession = hour >= 8 && hour < 17;
      } else if (sessionType === "asian") {
        inSession = hour >= 0 && hour < 9;
      } else if (sessionType === "both") {
        inSession = (hour >= 8 && hour < 17) || (hour >= 0 && hour < 9);
      }

      if (!inSession) return "HOLD";

      const recentPrices = prices.slice(index - lookbackPeriod, index);
      const resistance = Math.max(...recentPrices);
      const support = Math.min(...recentPrices);
      const currentPrice = prices[index];

      if (currentPrice > resistance * (1 + breakoutThreshold)) {
        return "BUY";
      }
      if (currentPrice < support * (1 - breakoutThreshold)) {
        return "SELL";
      }
      return "HOLD";
    };

    // 4. Price Action Strategy (Pin Bar, Engulfing, Break & Retest)
    const getPriceActionSignal = (index, params) => {
      const pinBarThreshold = params.pinBarThreshold || 0.6;
      const engulfingSize = (params.engulfingSize || 0.3) / 100;
      const retestTolerance = (params.retestTolerance || 0.2) / 100;
      const lookbackCandles = params.lookbackCandles || 20;

      if (index < lookbackCandles + 2 || !candlestickData[index]) return "HOLD";

      const currentCandle = candlestickData[index];
      const prevCandle = candlestickData[index - 1];

      const open = parseFloat(currentCandle.open);
      const high = parseFloat(currentCandle.high);
      const low = parseFloat(currentCandle.low);
      const close = parseFloat(currentCandle.close);

      const prevOpen = parseFloat(prevCandle.open);
      const prevHigh = parseFloat(prevCandle.high);
      const prevLow = parseFloat(prevCandle.low);
      const prevClose = parseFloat(prevCandle.close);

      const bodySize = Math.abs(close - open);
      const candleRange = high - low;
      const upperWick = high - Math.max(open, close);
      const lowerWick = Math.min(open, close) - low;

      // Pin Bar detection
      const isBullishPin = candleRange > 0 && lowerWick / candleRange > pinBarThreshold &&
        close > open &&
        upperWick / candleRange < 0.3;
      const isBearishPin = candleRange > 0 && upperWick / candleRange > pinBarThreshold &&
        close < open &&
        lowerWick / candleRange < 0.3;

      // Engulfing pattern detection
      const isBullishEngulfing = prevClose < prevOpen &&
        close > open &&
        open < prevClose &&
        close > prevOpen &&
        bodySize > Math.abs(prevClose - prevOpen) * (1 + engulfingSize);
      const isBearishEngulfing = prevClose > prevOpen &&
        close < open &&
        open > prevClose &&
        close < prevOpen &&
        bodySize > Math.abs(prevOpen - prevClose) * (1 + engulfingSize);

      // Break & Retest detection
      const lookbackPrices = prices.slice(Math.max(0, index - lookbackCandles), index);
      if (lookbackPrices.length > 0) {
        const support = Math.min(...lookbackPrices);
        const resistance = Math.max(...lookbackPrices);
        const currentPrice = close;

        const isBreakAboveRetest = currentPrice > resistance * (1 - retestTolerance) &&
          currentPrice < resistance * (1 + retestTolerance) &&
          low <= resistance;
        const isBreakBelowRetest = currentPrice < support * (1 + retestTolerance) &&
          currentPrice > support * (1 - retestTolerance) &&
          high >= support;

        if (isBullishPin || isBullishEngulfing || isBreakAboveRetest) {
          return "BUY";
        }
        if (isBearishPin || isBearishEngulfing || isBreakBelowRetest) {
          return "SELL";
        }
      }

      return "HOLD";
    };

    // 5. MACD Trend Reversal Strategy
    const getMACDReversalSignal = (index, params) => {
      const fastPeriod = params.fastPeriod || 12;
      const slowPeriod = params.slowPeriod || 26;
      const signalPeriod = params.signalPeriod || 9;
      const histogramThreshold = params.histogramThreshold || 0;

      if (index < slowPeriod + signalPeriod) return "HOLD";

      const fastEMA = calculateEMALocal(prices, index, fastPeriod);
      const slowEMA = calculateEMALocal(prices, index, slowPeriod);
      const macd = fastEMA - slowEMA;

      // Calculate signal line (EMA of MACD)
      const macdValues = [];
      for (let i = index - signalPeriod; i <= index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        macdValues.push(fEMA - sEMA);
      }
      const signalLine = macdValues.reduce((a, b) => a + b) / macdValues.length;

      const histogram = macd - signalLine;

      if (index < slowPeriod + signalPeriod + 1) return "HOLD";

      const prevFastEMA = calculateEMALocal(prices, index - 1, fastPeriod);
      const prevSlowEMA = calculateEMALocal(prices, index - 1, slowPeriod);
      const prevMACD = prevFastEMA - prevSlowEMA;

      const prevMacdValues = [];
      for (let i = index - signalPeriod - 1; i < index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        prevMacdValues.push(fEMA - sEMA);
      }
      const prevSignalLine = prevMacdValues.reduce((a, b) => a + b) / prevMacdValues.length;
      const prevHistogram = prevMACD - prevSignalLine;

      if (macd > signalLine && prevMACD <= prevSignalLine && histogram > histogramThreshold) {
        return "BUY";
      }
      if (macd < signalLine && prevMACD >= prevSignalLine && histogram < -histogramThreshold) {
        return "SELL";
      }
      return "HOLD";
    };

    // Generate signals for each candle
    for (let i = minLookback; i < candlestickData.length; i++) {
      let signal = "HOLD";

      switch (selectedStrategy) {
        case "ma_crossover":
          signal = getMACrossoverSignal(i, strategyParams);
          break;
        case "rsi_ma_confluence":
          signal = getRSIMAConfluenceSignal(i, strategyParams);
          break;
        case "breakout_strategy":
          signal = getBreakoutStrategySignal(i, strategyParams);
          break;
        case "price_action":
          signal = getPriceActionSignal(i, strategyParams);
          break;
        case "macd_reversal":
          signal = getMACDReversalSignal(i, strategyParams);
          break;
        default:
          signal = "HOLD";
      }

      if (signal !== "HOLD") {
        signals.push({
          time: candlestickData[i].time,
          price: prices[i],
          signal: signal,
          candleIndex: i,
          timestamp: new Date(candlestickData[i].time * 1000).toISOString(),
        });
      }
    }

    return signals;
  };

  // Test animation with strategy signals
  const runTestAnimationWithSignals = async () => {
    if (
      !candleSeriesRef.current ||
      !historicalData.length ||
      !selectedStrategy
    ) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    const testData = historicalData
      .map((candle) => ({
        time: Math.floor(new Date(candle.timestamp).getTime() / 1000),
        open: parseFloat(candle.open),
        high: parseFloat(candle.high),
        low: parseFloat(candle.low),
        close: parseFloat(candle.close),
      }))
      .sort((a, b) => a.time - b.time);

    // Generate strategy signals
    const signals = generateStrategySignals(testData);

    // Clear chart first
    candleSeriesRef.current.setData([]);
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Animate with signals
    for (let i = 0; i < testData.length; i++) {
      const currentData = testData.slice(0, i + 1);
      candleSeriesRef.current.setData(currentData);

      // Add signals that should appear at current candle
      const currentSignals = signals.filter(
        (signal) => signal.candleIndex === i
      );

      currentSignals.forEach((signal) => {
        try {
          if (signal.signal === "BUY") {
            const marker = candleSeriesRef.current.createPriceLine({
              price: signal.price,
              color: "#26a69a",
              lineWidth: 3,
              lineStyle: 0,
              axisLabelVisible: true,
              title: `BUY ${signal.price.toFixed(2)}`,
            });
            buyMarkersRef.current.push(marker);
          } else if (signal.signal === "SELL") {
            const marker = candleSeriesRef.current.createPriceLine({
              price: signal.price,
              color: "#ef5350",
              lineWidth: 3,
              lineStyle: 0,
              axisLabelVisible: true,
              title: `SELL ${signal.price.toFixed(2)}`,
            });
            sellMarkersRef.current.push(marker);
          }
        } catch (error) {
          console.error("Error creating signal marker:", error);
        }
      });

      // Update progress
      setCurrentBacktestIndex(i);
      setProgress((i / testData.length) * 100);

      await new Promise((resolve) => setTimeout(resolve, 30)); // 30ms delay
    }

    setProgress(100);
  };

  // Show all strategy signals at once (no animation)
  const showAllStrategySignals = () => {
    if (
      !candleSeriesRef.current ||
      !historicalData.length ||
      !selectedStrategy
    ) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    const testData = historicalData
      .map((candle) => ({
        time: Math.floor(new Date(candle.timestamp).getTime() / 1000),
        open: parseFloat(candle.open),
        high: parseFloat(candle.high),
        low: parseFloat(candle.low),
        close: parseFloat(candle.close),
      }))
      .sort((a, b) => a.time - b.time);

    // Generate strategy signals
    const signals = generateStrategySignals(testData);

    // Show all data at once
    candleSeriesRef.current.setData(testData);

    // Clear existing markers
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Add all signals
    signals.forEach((signal) => {
      try {
        if (signal.signal === "BUY") {
          const marker = candleSeriesRef.current.createPriceLine({
            price: signal.price,
            color: "#26a69a",
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: `BUY ${signal.price.toFixed(2)}`,
          });
          buyMarkersRef.current.push(marker);
        } else if (signal.signal === "SELL") {
          const marker = candleSeriesRef.current.createPriceLine({
            price: signal.price,
            color: "#ef5350",
            lineWidth: 3,
            lineStyle: 0,
            axisLabelVisible: true,
            title: `SELL ${signal.price.toFixed(2)}`,
          });
          sellMarkersRef.current.push(marker);
        }
      } catch (error) {
        console.error("Error creating signal marker:", error);
      }
    });

    // Update progress
    setCurrentBacktestIndex(testData.length - 1);
    setProgress(100);
  };

  // Render strategy documentation
  const renderStrategyDocumentation = (strategyId) => {
    const strategy = strategies.find((s) => s.id === strategyId);
    if (!strategy || !strategy.documentation) return null;

    const doc = strategy.documentation;
    const riskColor = {
      Low: "success",
      Medium: "warning",
      High: "danger",
      "Very High": "danger",
    };

    return (
      <Modal
        show={showDocumentation}
        onHide={() => setShowDocumentation(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <BarChart3 size={20} className="me-2" />
            {strategy.name} Documentation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="strategy-docs">
            <div className="mb-4">
              <h5>Overview</h5>
              <p className="text-muted">{doc.overview}</p>
            </div>

            <div className="mb-4">
              <h5>Parameters</h5>
              <div className="table-responsive">
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(doc.parameters).map(([key, value]) => (
                      <tr key={key}>
                        <td className="fw-semibold">{key}</td>
                        <td>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="mb-4">
              <h5>Trading Signals</h5>
              <Row>
                <Col md={6}>
                  <div className="p-3 border rounded bg-success bg-opacity-10">
                    <h6 className="text-success">BUY Signal</h6>
                    <p className="mb-0 small">{doc.signals.buy}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 border rounded bg-danger bg-opacity-10">
                    <h6 className="text-danger">SELL Signal</h6>
                    <p className="mb-0 small">{doc.signals.sell}</p>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="mb-4">
              <h5>Strategy Information</h5>
              <Row>
                <Col md={6}>
                  <div className="p-3 border rounded">
                    <h6 className="text-info">Best For</h6>
                    <p className="mb-0 small">{doc.bestFor}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 border rounded">
                    <h6 className="">Risk Level</h6>
                    <Badge bg={riskColor[doc.riskLevel]} className="fs-6">
                      {doc.riskLevel}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDocumentation(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Convert strategy builder to JavaScript code
  const generateStrategyCode = () => {
    let code = `function customStrategy(prices, volumes, index) {\n`;

    // Add indicator calculations
    const indicators = new Set();
    strategyBuilder.conditions.forEach((condition) => {
      if (condition.type === "indicator") {
        indicators.add(condition.indicator);
      }
    });

    indicators.forEach((indicator) => {
      const condition = strategyBuilder.conditions.find(
        (c) => c.indicator === indicator
      );
      if (condition) {
        code += `  // Calculate ${indicator.toUpperCase()}\n`;
        code += `  const ${indicator}Period = ${condition.period};\n`;
        code += `  if (index < ${indicator}Period) return 'HOLD';\n`;

        if (indicator === "sma") {
          code += `  const ${indicator} = prices.slice(index - ${indicator}Period, index).reduce((a, b) => a + b) / ${indicator}Period;\n`;
        } else if (indicator === "ema") {
          code += `  const ${indicator} = calculateEMA(prices, index, ${indicator}Period);\n`;
        } else if (indicator === "rsi") {
          code += `  const ${indicator} = calculateRSI(prices, index, ${indicator}Period);\n`;
        }
        code += `\n`;
      }
    });

    // Add condition checks
    code += `  // Check conditions\n`;
    strategyBuilder.conditions.forEach((condition, i) => {
      if (condition.type === "indicator") {
        const operator = condition.operator;
        const value = condition.value;
        const conditionType = condition.condition;

        if (conditionType === "price") {
          code += `  const condition${i + 1
            } = prices[index] ${operator} ${value};\n`;
        } else if (conditionType === "indicator") {
          code += `  const condition${i + 1} = ${condition.indicator
            } ${operator} ${value};\n`;
        } else if (conditionType === "volume") {
          code += `  const condition${i + 1
            } = volumes[index] ${operator} ${value};\n`;
        }
      }
    });

    // Add action logic
    code += `\n  // Execute actions\n`;
    strategyBuilder.actions.forEach((action) => {
      if (action.condition === "all") {
        const conditionChecks = strategyBuilder.conditions
          .map((_, i) => `condition${i + 1}`)
          .join(" && ");
        code += `  if (${conditionChecks}) {\n`;
        code += `    return '${action.signal}';\n`;
        code += `  }\n`;
      }
    });

    code += `\n  return 'HOLD';\n`;
    code += `}\n\n`;

    // Add helper functions
    if (indicators.has("ema")) {
      code += `function calculateEMA(prices, index, period) {\n`;
      code += `  const multiplier = 2 / (period + 1);\n`;
      code += `  let ema = prices[index - period];\n`;
      code += `  for (let i = index - period + 1; i <= index; i++) {\n`;
      code += `    ema = (prices[i] * multiplier) + (ema * (1 - multiplier));\n`;
      code += `  }\n`;
      code += `  return ema;\n`;
      code += `}\n\n`;
    }

    if (indicators.has("rsi")) {
      code += `function calculateRSI(prices, index, period) {\n`;
      code += `  let gains = 0, losses = 0;\n`;
      code += `  for (let i = index - period + 1; i <= index; i++) {\n`;
      code += `    const change = prices[i] - prices[i-1];\n`;
      code += `    if (change > 0) gains += change;\n`;
      code += `    else losses += Math.abs(change);\n`;
      code += `  }\n`;
      code += `  const avgGain = gains / period;\n`;
      code += `  const avgLoss = losses / period;\n`;
      code += `  if (avgLoss === 0) return 100;\n`;
      code += `  const rs = avgGain / avgLoss;\n`;
      code += `  return 100 - (100 / (1 + rs));\n`;
      code += `}\n`;
    }

    return code;
  };

  const symbols = [
    "BTCUSD",
    "ETHUSD",
    "ADAUSD",
    "SOLUSD",
    "DOGEUSD",
    "MATICUSD",
    "DOTUSD",
    "AVAXUSD",
    "LINKUSD",
    "UNIUSD",
    "BNBUSD",
    "XRPUSD",
    "LTCUSD",
    "BCHUSD",
    "EOSUSD",
    "TRXUSD",
    "XLMUSD",
    "VETUSD",
    "FILUSD",
    "ATOMUSD",
  ];

  const timeframes = [
    { value: "1m", label: "1 Minute" },
    { value: "3m", label: "3 Minutes" },
    { value: "5m", label: "5 Minutes" },
    { value: "15m", label: "15 Minutes" },
    { value: "30m", label: "30 Minutes" },
    { value: "1h", label: "1 Hour" },
    { value: "2h", label: "2 Hours" },
    { value: "4h", label: "4 Hours" },
    { value: "6h", label: "6 Hours" },
    { value: "12h", label: "12 Hours" },
    { value: "1d", label: "1 Day" },
    { value: "3d", label: "3 Days" },
    { value: "1w", label: "1 Week" },
  ];

  // Set default dates
  useEffect(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(oneMonthAgo.toISOString().split("T")[0]);
  }, []);

  // Initialize candlestick chart when historical data changes
  useEffect(() => {
    if (showCandlestickChart && historicalData.length > 0) {
      initializeCandlestickChart();
    }
  }, [showCandlestickChart, historicalData]);

  // Initialize candlestick chart (empty for animation)
  const initializeCandlestickChart = () => {
    if (!chartContainerRef.current) {
      return;
    }

    try {
      // Clean up existing chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 600,
        layout: {
          background: { color: "#ffffff" },
          textColor: "#333",
        },
        grid: {
          vertLines: { color: "#f0f0f0" },
          horzLines: { color: "#f0f0f0" },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: "#cccccc",
        },
        timeScale: {
          borderColor: "#cccccc",
          timeVisible: true,
          secondsVisible: false,
        },
      });

      const candleSeries = chart.addCandlestickSeries({
        upColor: "#26a69a",
        downColor: "#ef5350",
        borderUpColor: "#26a69a",
        borderDownColor: "#ef5350",
        wickUpColor: "#26a69a",
        wickDownColor: "#ef5350",
      });

      // Start with empty data for animation
      candleSeries.setData([]);

      chartRef.current = chart;
      candleSeriesRef.current = candleSeries;

      // Handle resize
      const handleResize = () => {
        if (chart && chartContainerRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        chart.remove();
      };
    } catch (error) {
      console.error("Error initializing candlestick chart:", error);
      alert("Error initializing chart: " + error.message);
    }
  };

  // Animate candlestick data progressively
  const animateCandlestickData = async (candlestickData, trades = []) => {
    if (!candleSeriesRef.current) {
      console.error("CandleSeriesRef is null!");
      return;
    }

    if (!candlestickData.length) {
      console.error("No candlestick data provided!");
      return;
    }

    // Clear existing data and markers
    candleSeriesRef.current.setData([]);
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    // Animate candles progressively - ONE CANDLE AT A TIME
    for (let i = 0; i < candlestickData.length; i++) {
      // Only show current candle + previous candles (progressive build-up)
      const currentData = candlestickData.slice(0, i + 1);

      try {
        // Set data with only current candles
        candleSeriesRef.current.setData(currentData);

        // Update progress
        setCurrentBacktestIndex(i);
        setProgress((i / candlestickData.length) * 100);

        // Add trade markers for current trades only
        const currentTrades = trades.filter((trade) => {
          const tradeTime = Math.floor(
            new Date(trade.timestamp).getTime() / 1000
          );
          return tradeTime <= candlestickData[i].time;
        });

        // Update trade markers
        if (currentTrades.length > 0) {
          // Clear existing markers
          buyMarkersRef.current.forEach((marker) =>
            candleSeriesRef.current.removePriceLine(marker)
          );
          sellMarkersRef.current.forEach((marker) =>
            candleSeriesRef.current.removePriceLine(marker)
          );
          buyMarkersRef.current = [];
          sellMarkersRef.current = [];

          // Add new markers
          currentTrades.forEach((trade) => {
            try {
              if (trade.type === "OPEN_LONG" || trade.type === "CLOSE_SHORT") {
                const marker = candleSeriesRef.current.createPriceLine({
                  price: trade.price,
                  color: "#26a69a",
                  lineWidth: 2,
                  lineStyle: 0,
                  axisLabelVisible: true,
                  title: `BUY ${trade.price.toFixed(2)}`,
                });
                buyMarkersRef.current.push(marker);
              } else if (
                trade.type === "OPEN_SHORT" ||
                trade.type === "CLOSE_LONG"
              ) {
                const marker = candleSeriesRef.current.createPriceLine({
                  price: trade.price,
                  color: "#ef5350",
                  lineWidth: 2,
                  lineStyle: 0,
                  axisLabelVisible: true,
                  title: `SELL ${trade.price.toFixed(2)}`,
                });
                sellMarkersRef.current.push(marker);
              }
            } catch (error) {
              console.error("Error creating trade marker:", error);
            }
          });
        }

        // Animation delay - adjustable speed
        await new Promise((resolve) => setTimeout(resolve, animationSpeed));

        // Break if backtest is stopped
        if (!isBacktestRunning) {
          break;
        }
      } catch (error) {
        console.error(`Error setting data at candle ${i}:`, error);
        break;
      }
    }
  };

  // Add trade markers to chart
  const addTradeMarkers = (trades) => {
    if (!candleSeriesRef.current || !trades.length) return;

    // Clear existing markers
    buyMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    sellMarkersRef.current.forEach((marker) =>
      candleSeriesRef.current.removePriceLine(marker)
    );
    buyMarkersRef.current = [];
    sellMarkersRef.current = [];

    trades.forEach((trade) => {
      const time = Math.floor(new Date(trade.timestamp).getTime() / 1000);

      if (trade.type === "OPEN_LONG" || trade.type === "CLOSE_SHORT") {
        const marker = candleSeriesRef.current.createPriceLine({
          price: trade.price,
          color: "#26a69a",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `BUY ${trade.price.toFixed(2)}`,
        });
        buyMarkersRef.current.push(marker);
      } else if (trade.type === "OPEN_SHORT" || trade.type === "CLOSE_LONG") {
        const marker = candleSeriesRef.current.createPriceLine({
          price: trade.price,
          color: "#ef5350",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `SELL ${trade.price.toFixed(2)}`,
        });
        sellMarkersRef.current.push(marker);
      }
    });
  };

  // Animated backtest execution
  const runAnimatedBacktest = async () => {
    if (!selectedStrategy || !historicalData.length) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    setIsBacktestRunning(true);
    setCurrentBacktestIndex(0);
    setProgress(0);

    try {
      // Initialize empty candlestick chart
      initializeCandlestickChart();
      setShowCandlestickChart(true);

      // Prepare candlestick data
      const candlestickData = historicalData
        .map((candle) => {
          const timestamp = new Date(candle.timestamp);
          if (isNaN(timestamp.getTime())) {
            console.warn("Invalid timestamp:", candle.timestamp);
            return null;
          }

          return {
            time: Math.floor(timestamp.getTime() / 1000),
            open: parseFloat(candle.open) || 0,
            high: parseFloat(candle.high) || 0,
            low: parseFloat(candle.low) || 0,
            close: parseFloat(candle.close) || 0,
          };
        })
        .filter((item) => item !== null)
        .sort((a, b) => a.time - b.time);

      // Run backtest simulation
      const results = await simulateAnimatedBacktest();
      setBacktestResults(results);

      // Start candlestick animation with trades
      await animateCandlestickData(candlestickData, results.trades);

      setProgress(100);
    } catch (error) {
      console.error("Animated backtest error:", error);
    }

    setIsBacktestRunning(false);
  };

  // Simulate animated backtest
  const simulateAnimatedBacktest = async () => {
    const trades = [];
    let capital = initialCapital;
    let position = 0;
    let totalTrades = 0;
    let winningTrades = 0;
    let totalProfit = 0;
    let maxDrawdown = 0;
    let peakCapital = capital;

    // Get strategy-specific parameters
    const strategyParams = advancedSettings[selectedStrategy] || {};
    const stopLossPercent = advancedSettings.stopLoss;
    const takeProfitPercent = advancedSettings.takeProfit;
    const commission = advancedSettings.commission / 100;
    const slippage = advancedSettings.slippage / 100;
    const maxTrades = advancedSettings.maxTrades;
    const leverage = advancedSettings.leverage;
    const allowLongTrading = advancedSettings.allowLongTrading;
    const allowShortSelling = advancedSettings.allowShortSelling;
    const strategyType = advancedSettings.strategyType;
    const customStrategy = advancedSettings.customStrategy;
    const customStrategyLanguage = advancedSettings.customStrategyLanguage;

    const prices = historicalData.map((candle) => candle.close);
    const volumes = historicalData.map((candle) => candle.volume);

    // Helper function to calculate EMA (corrected calculation)
    const calculateEMALocal = (prices, index, period) => {
      if (index < period - 1) return null;
      
      // Initialize EMA with SMA for the first period
      let sum = 0;
      for (let i = index - period + 1; i <= index; i++) {
        sum += prices[i];
      }
      let ema = sum / period;
      
      // Calculate EMA using the multiplier
      const multiplier = 2 / (period + 1);
      
      // Continue from the next index if we have more data
      for (let i = index - period + 2; i <= index; i++) {
        ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
      }
      
      return ema;
    };

    // Helper function to calculate RSI (using Wilder's smoothing)
    const calculateRSILocal = (prices, index, period) => {
      if (index < period) return null;
      
      let avgGain = 0;
      let avgLoss = 0;
      
      // Calculate initial average gain and loss
      for (let i = index - period + 1; i <= index; i++) {
        const change = prices[i] - prices[i - 1];
        if (change > 0) {
          avgGain += change;
        } else {
          avgLoss += Math.abs(change);
        }
      }
      
      avgGain = avgGain / period;
      avgLoss = avgLoss / period;
      
      // Use Wilder's smoothing for subsequent calculations
      if (index > period) {
        const currentChange = prices[index] - prices[index - 1];
        const currentGain = currentChange > 0 ? currentChange : 0;
        const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;
        
        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
      }
      
      if (avgLoss === 0) return 100;
      
      const rs = avgGain / avgLoss;
      return 100 - (100 / (1 + rs));
    };

    // Determine minimum lookback period based on strategy
    const minLookback = Math.max(
      strategyParams.fastEMA || 50,
      strategyParams.slowEMA || 200,
      strategyParams.rsiPeriod || 14,
      strategyParams.emaPeriod || 20,
      strategyParams.lookbackPeriod || 20,
      strategyParams.lookbackCandles || 20,
      strategyParams.slowPeriod || 26,
      strategyParams.signalPeriod || 9
    );

    // Strategy execution functions
    const executeCustomStrategy = (index) => {
      if (selectedStrategy === "custom_strategy" && customStrategy.trim()) {
        try {
          if (customStrategyLanguage === "python") {
            alert(
              "Python strategy execution requires backend support. Please use JavaScript for now."
            );
            return "HOLD";
          } else {
            const func = new Function(
              "prices",
              "volumes",
              "index",
              customStrategy
            );
            const result = func(prices, volumes, index);

            if (result !== "BUY" && result !== "SELL" && result !== "HOLD") {
              console.warn("Custom strategy returned invalid result:", result);
              return "HOLD";
            }

            return result;
          }
        } catch (error) {
          console.error("Custom strategy error:", error);
          return "HOLD";
        }
      } else if (selectedStrategy === "strategy_builder") {
        try {
          const generatedCode = generateStrategyCode();
          const func = new Function(
            "prices",
            "volumes",
            "index",
            generatedCode
          );
          const result = func(prices, volumes, index);

          if (result !== "BUY" && result !== "SELL" && result !== "HOLD") {
            console.warn("Strategy builder returned invalid result:", result);
            return "HOLD";
          }

          return result;
        } catch (error) {
          console.error("Strategy builder error:", error);
          return "HOLD";
        }
      }
      return null;
    };

    // Strategy-specific signal generation - 5 New Strategies
    const getStrategySignal = (index) => {
      const currentPrice = prices[index];

      switch (selectedStrategy) {
        case "ma_crossover":
          return getMACrossoverSignal(index, strategyParams);
        case "rsi_ma_confluence":
          return getRSIMAConfluenceSignal(index, strategyParams);
        case "breakout_strategy":
          return getBreakoutStrategySignal(index, strategyParams);
        case "price_action":
          return getPriceActionSignal(index, strategyParams);
        case "macd_reversal":
          return getMACDReversalSignal(index, strategyParams);
        default:
          return "HOLD";
      }
    };

    // 1. Moving Average Crossover Strategy (50 EMA + 200 EMA)
    const getMACrossoverSignal = (index, params) => {
      const fastEMA = params.fastEMA || 50;
      const slowEMA = params.slowEMA || 200;

      if (index < slowEMA) return "HOLD";

      const fastEmaValue = calculateEMALocal(prices, index, fastEMA);
      const slowEmaValue = calculateEMALocal(prices, index, slowEMA);

      if (index < slowEMA + 1) return "HOLD";
      const prevFastEma = calculateEMALocal(prices, index - 1, fastEMA);
      const prevSlowEma = calculateEMALocal(prices, index - 1, slowEMA);

      if (fastEmaValue > slowEmaValue && prevFastEma <= prevSlowEma) {
        return "BUY";
      }
      if (fastEmaValue < slowEmaValue && prevFastEma >= prevSlowEma) {
        return "SELL";
      }
      return "HOLD";
    };

    // 2. RSI + MA Confluence Strategy (RSI 14 + 20 EMA)
    const getRSIMAConfluenceSignal = (index, params) => {
      const rsiPeriod = params.rsiPeriod || 14;
      const emaPeriod = params.emaPeriod || 20;
      const oversoldLevel = params.oversoldLevel || 30;
      const overboughtLevel = params.overboughtLevel || 70;

      if (index < Math.max(rsiPeriod, emaPeriod) + 1) return "HOLD";

      const rsi = calculateRSILocal(prices, index, rsiPeriod);
      const ema = calculateEMALocal(prices, index, emaPeriod);
      const currentPrice = prices[index];

      if (rsi < oversoldLevel && currentPrice > ema) {
        return "BUY";
      }
      if (rsi > overboughtLevel && currentPrice < ema) {
        return "SELL";
      }
      return "HOLD";
    };

    // 3. Breakout Strategy (London/Asian Breakout)
    const getBreakoutStrategySignal = (index, params) => {
      const lookbackPeriod = params.lookbackPeriod || 20;
      const breakoutThreshold = (params.breakoutThreshold || 0.5) / 100;
      const sessionType = params.sessionType || "london";

      if (index < lookbackPeriod) return "HOLD";

      const currentTime = new Date(historicalData[index].timestamp);
      const hour = currentTime.getUTCHours();

      let inSession = false;
      if (sessionType === "london") {
        inSession = hour >= 8 && hour < 17;
      } else if (sessionType === "asian") {
        inSession = hour >= 0 && hour < 9;
      } else if (sessionType === "both") {
        inSession = (hour >= 8 && hour < 17) || (hour >= 0 && hour < 9);
      }

      if (!inSession) return "HOLD";

      const recentPrices = prices.slice(index - lookbackPeriod, index);
      const resistance = Math.max(...recentPrices);
      const support = Math.min(...recentPrices);
      const currentPrice = prices[index];

      if (currentPrice > resistance * (1 + breakoutThreshold)) {
        return "BUY";
      }
      if (currentPrice < support * (1 - breakoutThreshold)) {
        return "SELL";
      }
      return "HOLD";
    };

    // 4. Price Action Strategy (Pin Bar, Engulfing, Break & Retest)
    const getPriceActionSignal = (index, params) => {
      const pinBarThreshold = params.pinBarThreshold || 0.6;
      const engulfingSize = (params.engulfingSize || 0.3) / 100;
      const retestTolerance = (params.retestTolerance || 0.2) / 100;
      const lookbackCandles = params.lookbackCandles || 20;

      if (index < lookbackCandles + 2) return "HOLD";

      const currentCandle = historicalData[index];
      const prevCandle = historicalData[index - 1];

      const open = parseFloat(currentCandle.open);
      const high = parseFloat(currentCandle.high);
      const low = parseFloat(currentCandle.low);
      const close = parseFloat(currentCandle.close);

      const prevOpen = parseFloat(prevCandle.open);
      const prevClose = parseFloat(prevCandle.close);

      const bodySize = Math.abs(close - open);
      const candleRange = high - low;
      const upperWick = high - Math.max(open, close);
      const lowerWick = Math.min(open, close) - low;

      const isBullishPin = candleRange > 0 && lowerWick / candleRange > pinBarThreshold &&
        close > open &&
        upperWick / candleRange < 0.3;
      const isBearishPin = candleRange > 0 && upperWick / candleRange > pinBarThreshold &&
        close < open &&
        lowerWick / candleRange < 0.3;

      const isBullishEngulfing = prevClose < prevOpen &&
        close > open &&
        open < prevClose &&
        close > prevOpen &&
        bodySize > Math.abs(prevClose - prevOpen) * (1 + engulfingSize);
      const isBearishEngulfing = prevClose > prevOpen &&
        close < open &&
        open > prevClose &&
        close < prevOpen &&
        bodySize > Math.abs(prevOpen - prevClose) * (1 + engulfingSize);

      const lookbackPrices = prices.slice(Math.max(0, index - lookbackCandles), index);
      if (lookbackPrices.length > 0) {
        const support = Math.min(...lookbackPrices);
        const resistance = Math.max(...lookbackPrices);
        const currentPrice = close;

        const isBreakAboveRetest = currentPrice > resistance * (1 - retestTolerance) &&
          currentPrice < resistance * (1 + retestTolerance) &&
          low <= resistance;
        const isBreakBelowRetest = currentPrice < support * (1 + retestTolerance) &&
          currentPrice > support * (1 - retestTolerance) &&
          high >= support;

        if (isBullishPin || isBullishEngulfing || isBreakAboveRetest) {
          return "BUY";
        }
        if (isBearishPin || isBearishEngulfing || isBreakBelowRetest) {
          return "SELL";
        }
      }

      return "HOLD";
    };

    // 5. MACD Trend Reversal Strategy
    const getMACDReversalSignal = (index, params) => {
      const fastPeriod = params.fastPeriod || 12;
      const slowPeriod = params.slowPeriod || 26;
      const signalPeriod = params.signalPeriod || 9;
      const histogramThreshold = params.histogramThreshold || 0;

      if (index < slowPeriod + signalPeriod) return "HOLD";

      const fastEMA = calculateEMALocal(prices, index, fastPeriod);
      const slowEMA = calculateEMALocal(prices, index, slowPeriod);
      const macd = fastEMA - slowEMA;

      const macdValues = [];
      for (let i = index - signalPeriod; i <= index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        macdValues.push(fEMA - sEMA);
      }
      const signalLine = macdValues.reduce((a, b) => a + b) / macdValues.length;

      const histogram = macd - signalLine;

      if (index < slowPeriod + signalPeriod + 1) return "HOLD";

      const prevFastEMA = calculateEMALocal(prices, index - 1, fastPeriod);
      const prevSlowEMA = calculateEMALocal(prices, index - 1, slowPeriod);
      const prevMACD = prevFastEMA - prevSlowEMA;

      const prevMacdValues = [];
      for (let i = index - signalPeriod - 1; i < index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        prevMacdValues.push(fEMA - sEMA);
      }
      const prevSignalLine = prevMacdValues.reduce((a, b) => a + b) / prevMacdValues.length;
      const prevHistogram = prevMACD - prevSignalLine;

      if (macd > signalLine && prevMACD <= prevSignalLine && histogram > histogramThreshold) {
        return "BUY";
      }
      if (macd < signalLine && prevMACD >= prevSignalLine && histogram < -histogramThreshold) {
        return "SELL";
      }
      return "HOLD";
    };

    // Animated execution loop
    for (
      let i = minLookback;
      i < prices.length && totalTrades < maxTrades;
      i++
    ) {
      const currentPrice = prices[i];

      // Apply slippage
      const buyPrice = currentPrice * (1 + slippage);
      const sellPrice = currentPrice * (1 - slippage);

      // Get trading signal using strategy-specific function
      let signal = getStrategySignal(i);

      // Apply strategy type restrictions
      if (strategyType === "long" && signal === "SELL") {
        signal = "HOLD";
      } else if (strategyType === "short" && signal === "BUY") {
        signal = "HOLD";
      }

      // Apply trading permissions
      if (signal === "BUY" && !allowLongTrading) {
        signal = "HOLD";
      } else if (signal === "SELL" && !allowShortSelling) {
        signal = "HOLD";
      }

      // Check for stop loss or take profit on existing position
      if (position > 0) {
        const entryPrice = trades[trades.length - 1]?.price || currentPrice;
        const stopLossPrice = entryPrice * (1 - stopLossPercent / 100);
        const takeProfitPrice = entryPrice * (1 + takeProfitPercent / 100);

        if (currentPrice <= stopLossPrice || currentPrice >= takeProfitPrice) {
          const profit = position * sellPrice - position * entryPrice;
          const commissionCost =
            (position * sellPrice + position * entryPrice) * commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_LONG",
            price: sellPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: currentPrice <= stopLossPrice ? "STOP_LOSS" : "TAKE_PROFIT",
          });
          if (netProfit > 0) winningTrades++;
          position = 0;
          totalTrades++;
        }
      }

      if (position < 0) {
        const entryPrice = trades[trades.length - 1]?.price || currentPrice;
        const stopLossPrice = entryPrice * (1 + stopLossPercent / 100);
        const takeProfitPrice = entryPrice * (1 - takeProfitPercent / 100);

        if (currentPrice >= stopLossPrice || currentPrice <= takeProfitPrice) {
          const profit =
            Math.abs(position) * entryPrice - Math.abs(position) * buyPrice;
          const commissionCost =
            (Math.abs(position) * buyPrice + Math.abs(position) * entryPrice) *
            commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_SHORT",
            price: buyPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: currentPrice >= stopLossPrice ? "STOP_LOSS" : "TAKE_PROFIT",
          });
          if (netProfit > 0) winningTrades++;
          position = 0;
          totalTrades++;
        }
      }

      // Buy signal
      if (signal === "BUY" && position <= 0 && totalTrades < maxTrades) {
        if (position < 0) {
          const entryPrice = trades[trades.length - 1]?.price || currentPrice;
          const profit =
            Math.abs(position) * entryPrice - Math.abs(position) * buyPrice;
          const commissionCost =
            (Math.abs(position) * buyPrice + Math.abs(position) * entryPrice) *
            commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_SHORT",
            price: buyPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: "SIGNAL_CLOSE",
          });
          if (netProfit > 0) winningTrades++;
          totalTrades++;
        }

        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        position = tradeAmount / buyPrice;
        const commissionCost = tradeAmount * commission;
        capital -= tradeAmount + commissionCost;

        trades.push({
          type: "OPEN_LONG",
          price: buyPrice,
          amount: position,
          timestamp: historicalData[i].timestamp,
          commission: commissionCost,
        });
        totalTrades++;
      }

      // Sell signal
      if (signal === "SELL" && position >= 0 && totalTrades < maxTrades) {
        if (position > 0) {
          const entryPrice = trades[trades.length - 1]?.price || currentPrice;
          const profit = position * sellPrice - position * entryPrice;
          const commissionCost =
            (position * sellPrice + position * entryPrice) * commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_LONG",
            price: sellPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: "SIGNAL_CLOSE",
          });
          if (netProfit > 0) winningTrades++;
          totalTrades++;
        }

        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        position = -(tradeAmount / sellPrice);
        const commissionCost = tradeAmount * commission;
        capital -= tradeAmount + commissionCost;

        trades.push({
          type: "OPEN_SHORT",
          price: sellPrice,
          amount: Math.abs(position),
          timestamp: historicalData[i].timestamp,
          commission: commissionCost,
        });
        totalTrades++;
      }

      // Track drawdown
      if (capital > peakCapital) {
        peakCapital = capital;
      }
      const drawdown = (peakCapital - capital) / peakCapital;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }

      // Update progress and current index for animation
      setCurrentBacktestIndex(i);
      setProgress((i / prices.length) * 100);

      // Add small delay for animation effect (reduced for better performance)
      await new Promise((resolve) => setTimeout(resolve, 5));
    }

    // Calculate totalTrades from completed trades only (CLOSE actions)
    // This ensures it matches the Detailed Trade Report
    const completedTrades = trades.filter(
      (t) => t.type === "CLOSE_LONG" || t.type === "CLOSE_SHORT"
    );
    const actualTotalTrades = completedTrades.length;
    const actualWinningTrades = completedTrades.filter((t) => t.profit > 0).length;
    const actualWinRate = actualTotalTrades > 0 ? (actualWinningTrades / actualTotalTrades) * 100 : 0;
    const totalReturn = ((capital - initialCapital) / initialCapital) * 100;
    const sharpeRatio = totalReturn / (maxDrawdown * 100 || 1);
    const profitFactor = actualWinningTrades > 0 && actualTotalTrades - actualWinningTrades > 0
      ? actualWinningTrades / (actualTotalTrades - actualWinningTrades)
      : 0;
    const avgWin = actualWinningTrades > 0
      ? completedTrades
        .filter((t) => t.profit > 0)
        .reduce((sum, t) => sum + t.profit, 0) / actualWinningTrades
      : 0;
    const avgLoss = actualTotalTrades - actualWinningTrades > 0
      ? completedTrades
        .filter((t) => t.profit < 0)
        .reduce((sum, t) => sum + Math.abs(t.profit), 0) /
      (actualTotalTrades - actualWinningTrades)
      : 0;
    const maxConsecutiveWins = calculateMaxConsecutiveWins(completedTrades);
    const maxConsecutiveLosses = calculateMaxConsecutiveLosses(completedTrades);
    const totalCommission = trades.reduce(
      (sum, t) => sum + (t.commission || 0),
      0
    );
    const netProfit = totalProfit - totalCommission;

    return {
      totalTrades: actualTotalTrades,
      winningTrades: actualWinningTrades,
      winRate: actualWinRate,
      totalReturn,
      finalCapital: capital,
      maxDrawdown: maxDrawdown * 100,
      sharpeRatio,
      profitFactor,
      avgWin,
      avgLoss,
      maxConsecutiveWins,
      maxConsecutiveLosses,
      totalCommission,
      netProfit,
      trades,
      strategy: selectedStrategy,
      symbol: selectedSymbol,
      timeframe,
      period: `${startDate} to ${endDate}`,
      initialCapital,
      advancedSettings,
    };
  };

  const fetchHistoricalData = async () => {
    setIsLoading(true);
    setProgress(0);

    try {
      // Validate parameters
      const validation = DeltaExchangeService.validateBacktestParams({
        symbol: selectedSymbol,
        timeframe: timeframe,
        startDate: startDate,
        endDate: endDate,
        strategy: selectedStrategy,
        initialCapital: initialCapital,
        riskPerTrade: riskPerTrade,
      });

      if (!validation.isValid) {
        alert(
          "Please fix the following errors:\n" + validation.errors.join("\n")
        );
        setIsLoading(false);
        return;
      }

      // Fetch historical data using service
      const response = await DeltaExchangeService.getHistoricalData({
        symbol: selectedSymbol,
        timeframe: timeframe,
        startDate: startDate,
        endDate: endDate,
      });

      if (response.success) {
        // Sort data by timestamp to ensure proper order
        const sortedData = (response.data.candles || []).sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        setHistoricalData(sortedData);

        // Prepare chart data
        const chartData = prepareChartData(sortedData);
        setChartData(chartData);
        setShowChart(true);

        // Simulate progress
        for (let i = 0; i <= 100; i += 10) {
          setProgress(i);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } else {
        throw new Error(response.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
      alert("Error fetching historical data: " + error.message);

      // Use mock data for demonstration
      const mockData = generateMockHistoricalData();
      setHistoricalData(mockData);

      // Prepare chart data for mock data
      const chartData = prepareChartData(mockData);
      setChartData(chartData);
      setShowChart(true);

      setProgress(100);
    }

    setIsLoading(false);
  };

  const generateMockHistoricalData = () => {
    const data = [];
    const startPrice = 50000; // Starting price for BTCUSD
    let currentPrice = startPrice;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const interval = getIntervalMinutes(timeframe);

    for (
      let date = new Date(start);
      date <= end;
      date.setMinutes(date.getMinutes() + interval)
    ) {
      // Generate realistic price movement
      const change = (Math.random() - 0.5) * 0.02; // ±1% change
      currentPrice *= 1 + change;

      const high = currentPrice * (1 + Math.random() * 0.01);
      const low = currentPrice * (1 - Math.random() * 0.01);
      const volume = Math.random() * 1000000;

      data.push({
        timestamp: date.toISOString(),
        open: currentPrice,
        high: high,
        low: low,
        close: currentPrice,
        volume: volume,
        time: Math.floor(date.getTime() / 1000), // Delta Exchange format timestamp
      });
    }

    // Sort data by timestamp to ensure proper order
    return data.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  const getIntervalMinutes = (tf) => {
    const intervals = {
      "1m": 1,
      "3m": 3,
      "5m": 5,
      "15m": 15,
      "30m": 30,
      "1h": 60,
      "2h": 120,
      "4h": 240,
      "6h": 360,
      "12h": 720,
      "1d": 1440,
      "3d": 4320,
      "1w": 10080,
    };
    return intervals[tf] || 60;
  };

  const prepareChartData = (data, trades = []) => {
    if (!data || data.length === 0) return null;

    // Create better time labels - show every nth label to avoid overlap
    const step = Math.max(1, Math.floor(data.length / 10)); // Show max 10 labels
    const labels = data.map((candle, index) => {
      if (index % step === 0 || index === data.length - 1) {
        const date = new Date(candle.timestamp);
        return (
          date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          }) +
          " " +
          date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          })
        );
      }
      return "";
    });

    const prices = data.map((candle) => candle.close);
    const volumes = data.map((candle) => candle.volume);

    // Create trade markers
    const tradeMarkers = trades.map((trade) => {
      const candleIndex = data.findIndex(
        (candle) =>
          new Date(candle.timestamp).getTime() >=
          new Date(trade.timestamp).getTime()
      );
      return {
        x: candleIndex >= 0 ? candleIndex : 0,
        y: trade.price,
        type: trade.type,
        profit: trade.profit,
      };
    });

    return {
      labels: labels,
      datasets: [
        {
          label: "Price",
          data: prices,
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "rgba(75, 192, 192, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 5,
        },
        {
          label: "Volume",
          data: volumes,
          type: "bar",
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 1,
          yAxisID: "y1",
        },
      ],
      tradeMarkers: tradeMarkers,
    };
  };

  const calculateMaxConsecutiveWins = (trades) => {
    let maxWins = 0;
    let currentWins = 0;

    trades.forEach((trade) => {
      if (trade.profit > 0) {
        currentWins++;
        maxWins = Math.max(maxWins, currentWins);
      } else {
        currentWins = 0;
      }
    });

    return maxWins;
  };

  const calculateMaxConsecutiveLosses = (trades) => {
    let maxLosses = 0;
    let currentLosses = 0;

    trades.forEach((trade) => {
      if (trade.profit < 0) {
        currentLosses++;
        maxLosses = Math.max(maxLosses, currentLosses);
      } else {
        currentLosses = 0;
      }
    });

    return maxLosses;
  };

  // Clear all backtest data
  const clearAllData = () => {
    setBacktestResults(null);
    setHistoricalData([]);
    setChartData(null);
    setShowChart(false);
    setShowCandlestickChart(false);
    setProgress(0);
    setSelectedStrategy("");
    setStartDate("");
    setEndDate("");
    setCurrentBacktestIndex(0);

    // Clear localStorage
    localStorage.removeItem("strategyPresets");

    // Clear chart markers
    if (buyMarkersRef.current) buyMarkersRef.current = [];
    if (sellMarkersRef.current) sellMarkersRef.current = [];

    // Clear chart data if chart exists
    if (candleSeriesRef.current) {
      candleSeriesRef.current.setData([]);
    }

    alert("All backtest data has been cleared!");
  };

  const runBacktest = async () => {
    if (!selectedStrategy || !historicalData.length) {
      alert("Please select a strategy and fetch historical data first");
      return;
    }

    setIsLoading(true);
    setProgress(0);

    try {
      // Simulate backtesting process
      const results = await simulateBacktest();
      setBacktestResults(results);

      // Update chart with trade markers
      const updatedChartData = prepareChartData(historicalData, results.trades);
      setChartData(updatedChartData);

      // Simulate progress
      for (let i = 0; i <= 100; i += 5) {
        setProgress(i);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error("Backtest error:", error);
    }

    setIsLoading(false);
  };

  const simulateBacktest = async () => {
    // Simulate backtesting algorithm
    const trades = [];
    let capital = initialCapital;
    let position = 0;
    let totalTrades = 0;
    let winningTrades = 0;
    let totalProfit = 0;
    let maxDrawdown = 0;
    let peakCapital = capital;

    // Get strategy-specific parameters
    const strategyParams = advancedSettings[selectedStrategy] || {};
    const stopLossPercent = advancedSettings.stopLoss;
    const takeProfitPercent = advancedSettings.takeProfit;
    const commission = advancedSettings.commission / 100;
    const slippage = advancedSettings.slippage / 100;
    const maxTrades = advancedSettings.maxTrades;
    const leverage = advancedSettings.leverage;
    const allowLongTrading = advancedSettings.allowLongTrading;
    const allowShortSelling = advancedSettings.allowShortSelling;
    const strategyType = advancedSettings.strategyType;
    const customStrategy = advancedSettings.customStrategy;
    const customStrategyLanguage = advancedSettings.customStrategyLanguage;

    const prices = historicalData.map((candle) => candle.close);
    const volumes = historicalData.map((candle) => candle.volume);

    // Strategy execution functions
    const executeCustomStrategy = (index) => {
      if (selectedStrategy === "custom_strategy" && customStrategy.trim()) {
        try {
          if (customStrategyLanguage === "python") {
            // For Python, we would need a Python interpreter
            // For now, we'll show an alert that Python support needs backend implementation
            alert(
              "Python strategy execution requires backend support. Please use JavaScript for now."
            );
            return "HOLD";
          } else {
            // JavaScript execution
            const func = new Function(
              "prices",
              "volumes",
              "index",
              customStrategy
            );
            const result = func(prices, volumes, index);

            // Validate result
            if (result !== "BUY" && result !== "SELL" && result !== "HOLD") {
              console.warn("Custom strategy returned invalid result:", result);
              return "HOLD";
            }

            return result;
          }
        } catch (error) {
          console.error("Custom strategy error:", error);
          return "HOLD";
        }
      } else if (selectedStrategy === "strategy_builder") {
        try {
          const generatedCode = generateStrategyCode();
          const func = new Function(
            "prices",
            "volumes",
            "index",
            generatedCode
          );
          const result = func(prices, volumes, index);

          if (result !== "BUY" && result !== "SELL" && result !== "HOLD") {
            console.warn("Strategy builder returned invalid result:", result);
            return "HOLD";
          }

          return result;
        } catch (error) {
          console.error("Strategy builder error:", error);
          return "HOLD";
        }
      }
      return null;
    };

    // Strategy-specific signal generation
    const getStrategySignal = (index) => {
      const currentPrice = prices[index];

      switch (selectedStrategy) {
        case "ma_crossover":
          return getMACrossoverSignal(index, strategyParams);
        case "rsi_ma_confluence":
          return getRSIMAConfluenceSignal(index, strategyParams);
        case "breakout_strategy":
          return getBreakoutStrategySignal(index, strategyParams);
        case "price_action":
          return getPriceActionSignal(index, strategyParams);
        case "macd_reversal":
          return getMACDReversalSignal(index, strategyParams);
        default:
          return "HOLD";
      }
    };

    // Helper function to calculate EMA (corrected calculation)
    const calculateEMALocal = (prices, index, period) => {
      if (index < period - 1) return null;
      
      // Initialize EMA with SMA for the first period
      let sum = 0;
      for (let i = index - period + 1; i <= index; i++) {
        sum += prices[i];
      }
      let ema = sum / period;
      
      // Calculate EMA using the multiplier
      const multiplier = 2 / (period + 1);
      
      // Continue from the next index if we have more data
      for (let i = index - period + 2; i <= index; i++) {
        ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
      }
      
      return ema;
    };

    // Helper function to calculate RSI (using Wilder's smoothing)
    const calculateRSILocal = (prices, index, period) => {
      if (index < period) return null;
      
      let avgGain = 0;
      let avgLoss = 0;
      
      // Calculate initial average gain and loss
      for (let i = index - period + 1; i <= index; i++) {
        const change = prices[i] - prices[i - 1];
        if (change > 0) {
          avgGain += change;
        } else {
          avgLoss += Math.abs(change);
        }
      }
      
      avgGain = avgGain / period;
      avgLoss = avgLoss / period;
      
      // Use Wilder's smoothing for subsequent calculations
      if (index > period) {
        const currentChange = prices[index] - prices[index - 1];
        const currentGain = currentChange > 0 ? currentChange : 0;
        const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;
        
        avgGain = (avgGain * (period - 1) + currentGain) / period;
        avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
      }
      
      if (avgLoss === 0) return 100;
      
      const rs = avgGain / avgLoss;
      return 100 - (100 / (1 + rs));
    };

    // 1. Moving Average Crossover Strategy (50 EMA + 200 EMA)
    const getMACrossoverSignal = (index, params) => {
      const fastEMA = params.fastEMA || 50;
      const slowEMA = params.slowEMA || 200;

      if (index < slowEMA) return "HOLD";

      const fastEmaValue = calculateEMALocal(prices, index, fastEMA);
      const slowEmaValue = calculateEMALocal(prices, index, slowEMA);

      // Previous values for crossover detection
      if (index < slowEMA + 1) return "HOLD";
      const prevFastEma = calculateEMALocal(prices, index - 1, fastEMA);
      const prevSlowEma = calculateEMALocal(prices, index - 1, slowEMA);

      // Bullish crossover: fast EMA crosses above slow EMA
      if (fastEmaValue > slowEmaValue && prevFastEma <= prevSlowEma) {
        return "BUY";
      }
      // Bearish crossover: fast EMA crosses below slow EMA
      if (fastEmaValue < slowEmaValue && prevFastEma >= prevSlowEma) {
        return "SELL";
      }
      return "HOLD";
    };

    // 2. RSI + MA Confluence Strategy (RSI 14 + 20 EMA)
    const getRSIMAConfluenceSignal = (index, params) => {
      const rsiPeriod = params.rsiPeriod || 14;
      const emaPeriod = params.emaPeriod || 20;
      const oversoldLevel = params.oversoldLevel || 30;
      const overboughtLevel = params.overboughtLevel || 70;

      if (index < Math.max(rsiPeriod, emaPeriod) + 1) return "HOLD";

      const rsi = calculateRSILocal(prices, index, rsiPeriod);
      const ema = calculateEMALocal(prices, index, emaPeriod);
      const currentPrice = prices[index];

      // Buy: RSI oversold AND price above EMA (uptrend confirmation)
      if (rsi < oversoldLevel && currentPrice > ema) {
        return "BUY";
      }
      // Sell: RSI overbought AND price below EMA (downtrend confirmation)
      if (rsi > overboughtLevel && currentPrice < ema) {
        return "SELL";
      }
      return "HOLD";
    };

    // 3. Breakout Strategy (London/Asian Breakout)
    const getBreakoutStrategySignal = (index, params) => {
      const lookbackPeriod = params.lookbackPeriod || 20;
      const breakoutThreshold = (params.breakoutThreshold || 0.5) / 100; // Convert % to decimal
      const sessionType = params.sessionType || "london";

      if (index < lookbackPeriod) return "HOLD";

      // Check if current time is in the specified session
      const currentTime = new Date(historicalData[index].timestamp);
      const hour = currentTime.getUTCHours();

      let inSession = false;
      if (sessionType === "london") {
        // London session: 8:00 - 17:00 UTC
        inSession = hour >= 8 && hour < 17;
      } else if (sessionType === "asian") {
        // Asian session: 0:00 - 9:00 UTC
        inSession = hour >= 0 && hour < 9;
      } else if (sessionType === "both") {
        // Both sessions
        inSession = (hour >= 8 && hour < 17) || (hour >= 0 && hour < 9);
      }

      if (!inSession) return "HOLD";

      const recentPrices = prices.slice(index - lookbackPeriod, index);
      const resistance = Math.max(...recentPrices);
      const support = Math.min(...recentPrices);
      const currentPrice = prices[index];

      // Breakout above resistance
      if (currentPrice > resistance * (1 + breakoutThreshold)) {
        return "BUY";
      }
      // Breakdown below support
      if (currentPrice < support * (1 - breakoutThreshold)) {
        return "SELL";
      }
      return "HOLD";
    };

    // 4. Price Action Strategy (Pin Bar, Engulfing, Break & Retest)
    const getPriceActionSignal = (index, params) => {
      const pinBarThreshold = params.pinBarThreshold || 0.6;
      const engulfingSize = (params.engulfingSize || 0.3) / 100;
      const retestTolerance = (params.retestTolerance || 0.2) / 100;
      const lookbackCandles = params.lookbackCandles || 20;

      if (index < lookbackCandles + 2) return "HOLD";

      const currentCandle = historicalData[index];
      const prevCandle = historicalData[index - 1];

      const open = parseFloat(currentCandle.open);
      const high = parseFloat(currentCandle.high);
      const low = parseFloat(currentCandle.low);
      const close = parseFloat(currentCandle.close);

      const prevOpen = parseFloat(prevCandle.open);
      const prevHigh = parseFloat(prevCandle.high);
      const prevLow = parseFloat(prevCandle.low);
      const prevClose = parseFloat(prevCandle.close);

      const bodySize = Math.abs(close - open);
      const candleRange = high - low;
      const upperWick = high - Math.max(open, close);
      const lowerWick = Math.min(open, close) - low;

      // Pin Bar detection
      const isBullishPin = lowerWick / candleRange > pinBarThreshold &&
        close > open &&
        upperWick / candleRange < 0.3;
      const isBearishPin = upperWick / candleRange > pinBarThreshold &&
        close < open &&
        lowerWick / candleRange < 0.3;

      // Engulfing pattern detection
      const isBullishEngulfing = prevClose < prevOpen &&
        close > open &&
        open < prevClose &&
        close > prevOpen &&
        bodySize > (prevClose - prevOpen) * (1 + engulfingSize);
      const isBearishEngulfing = prevClose > prevOpen &&
        close < open &&
        open > prevClose &&
        close < prevOpen &&
        bodySize > (prevOpen - prevClose) * (1 + engulfingSize);

      // Break & Retest detection
      const lookbackPrices = prices.slice(index - lookbackCandles, index);
      const support = Math.min(...lookbackPrices);
      const resistance = Math.max(...lookbackPrices);
      const currentPrice = close;

      const isBreakAboveRetest = currentPrice > resistance * (1 - retestTolerance) &&
        currentPrice < resistance * (1 + retestTolerance) &&
        low <= resistance;
      const isBreakBelowRetest = currentPrice < support * (1 + retestTolerance) &&
        currentPrice > support * (1 - retestTolerance) &&
        high >= support;

      // Generate signals
      if (isBullishPin || isBullishEngulfing || isBreakAboveRetest) {
        return "BUY";
      }
      if (isBearishPin || isBearishEngulfing || isBreakBelowRetest) {
        return "SELL";
      }
      return "HOLD";
    };

    // 5. MACD Trend Reversal Strategy
    const getMACDReversalSignal = (index, params) => {
      const fastPeriod = params.fastPeriod || 12;
      const slowPeriod = params.slowPeriod || 26;
      const signalPeriod = params.signalPeriod || 9;
      const histogramThreshold = params.histogramThreshold || 0;

      if (index < slowPeriod + signalPeriod) return "HOLD";

      // Calculate MACD line
      const fastEMA = calculateEMALocal(prices, index, fastPeriod);
      const slowEMA = calculateEMALocal(prices, index, slowPeriod);
      const macd = fastEMA - slowEMA;

      // Calculate signal line (EMA of MACD)
      const macdValues = [];
      for (let i = index - signalPeriod; i <= index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        macdValues.push(fEMA - sEMA);
      }
      const signalLine = macdValues.reduce((a, b) => a + b) / macdValues.length;

      // Calculate histogram
      const histogram = macd - signalLine;

      // Previous values for crossover detection
      if (index < slowPeriod + signalPeriod + 1) return "HOLD";

      const prevFastEMA = calculateEMALocal(prices, index - 1, fastPeriod);
      const prevSlowEMA = calculateEMALocal(prices, index - 1, slowPeriod);
      const prevMACD = prevFastEMA - prevSlowEMA;

      const prevMacdValues = [];
      for (let i = index - signalPeriod - 1; i < index; i++) {
        const fEMA = calculateEMALocal(prices, i, fastPeriod);
        const sEMA = calculateEMALocal(prices, i, slowPeriod);
        prevMacdValues.push(fEMA - sEMA);
      }
      const prevSignalLine = prevMacdValues.reduce((a, b) => a + b) / prevMacdValues.length;
      const prevHistogram = prevMACD - prevSignalLine;

      // Bullish reversal: MACD crosses above signal AND histogram positive
      if (macd > signalLine && prevMACD <= prevSignalLine && histogram > histogramThreshold) {
        return "BUY";
      }
      // Bearish reversal: MACD crosses below signal AND histogram negative
      if (macd < signalLine && prevMACD >= prevSignalLine && histogram < -histogramThreshold) {
        return "SELL";
      }
      return "HOLD";
    };

    // Helper functions are already defined above (calculateEMA, calculateRSI)
    // Reusing them here

    // Determine minimum lookback period based on strategy
    const minLookback = Math.max(
      strategyParams.fastEMA || 50,
      strategyParams.slowEMA || 200,
      strategyParams.rsiPeriod || 14,
      strategyParams.emaPeriod || 20,
      strategyParams.lookbackPeriod || 20,
      strategyParams.lookbackCandles || 20,
      strategyParams.slowPeriod || 26,
      strategyParams.signalPeriod || 9
    );

    for (
      let i = minLookback;
      i < prices.length && totalTrades < maxTrades;
      i++
    ) {
      const currentPrice = prices[i];

      // Apply slippage
      const buyPrice = currentPrice * (1 + slippage);
      const sellPrice = currentPrice * (1 - slippage);

      // Get trading signal using strategy-specific function
      let signal = getStrategySignal(i);

      // Apply strategy type restrictions
      if (strategyType === "long" && signal === "SELL") {
        signal = "HOLD";
      } else if (strategyType === "short" && signal === "BUY") {
        signal = "HOLD";
      }

      // Apply trading permissions
      if (signal === "BUY" && !allowLongTrading) {
        signal = "HOLD";
      } else if (signal === "SELL" && !allowShortSelling) {
        signal = "HOLD";
      }

      // Check for stop loss or take profit on existing position
      if (position > 0) {
        const entryPrice = trades[trades.length - 1]?.price || currentPrice;
        const stopLossPrice = entryPrice * (1 - stopLossPercent / 100);
        const takeProfitPrice = entryPrice * (1 + takeProfitPercent / 100);

        if (currentPrice <= stopLossPrice || currentPrice >= takeProfitPrice) {
          // Close long position
          const profit = position * sellPrice - position * entryPrice;
          const commissionCost =
            (position * sellPrice + position * entryPrice) * commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_LONG",
            price: sellPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: currentPrice <= stopLossPrice ? "STOP_LOSS" : "TAKE_PROFIT",
          });
          if (netProfit > 0) winningTrades++;
          position = 0;
          totalTrades++;
        }
      }

      if (position < 0) {
        const entryPrice = trades[trades.length - 1]?.price || currentPrice;
        const stopLossPrice = entryPrice * (1 + stopLossPercent / 100);
        const takeProfitPrice = entryPrice * (1 - takeProfitPercent / 100);

        if (currentPrice >= stopLossPrice || currentPrice <= takeProfitPrice) {
          // Close short position
          const profit =
            Math.abs(position) * entryPrice - Math.abs(position) * buyPrice;
          const commissionCost =
            (Math.abs(position) * buyPrice + Math.abs(position) * entryPrice) *
            commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_SHORT",
            price: buyPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: currentPrice >= stopLossPrice ? "STOP_LOSS" : "TAKE_PROFIT",
          });
          if (netProfit > 0) winningTrades++;
          position = 0;
          totalTrades++;
        }
      }

      // Buy signal
      if (signal === "BUY" && position <= 0 && totalTrades < maxTrades) {
        if (position < 0) {
          // Close short position first
          const entryPrice = trades[trades.length - 1]?.price || currentPrice;
          const profit =
            Math.abs(position) * entryPrice - Math.abs(position) * buyPrice;
          const commissionCost =
            (Math.abs(position) * buyPrice + Math.abs(position) * entryPrice) *
            commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_SHORT",
            price: buyPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: "SIGNAL_CLOSE",
          });
          if (netProfit > 0) winningTrades++;
          totalTrades++;
        }

        // Open long position
        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        position = tradeAmount / buyPrice;
        const commissionCost = tradeAmount * commission;
        capital -= tradeAmount + commissionCost;

        trades.push({
          type: "OPEN_LONG",
          price: buyPrice,
          amount: position,
          timestamp: historicalData[i].timestamp,
          commission: commissionCost,
        });
        totalTrades++;
      }

      // Sell signal
      if (signal === "SELL" && position >= 0 && totalTrades < maxTrades) {
        if (position > 0) {
          // Close long position first
          const entryPrice = trades[trades.length - 1]?.price || currentPrice;
          const profit = position * sellPrice - position * entryPrice;
          const commissionCost =
            (position * sellPrice + position * entryPrice) * commission;
          const netProfit = profit - commissionCost;

          totalProfit += netProfit;
          capital += netProfit;
          trades.push({
            type: "CLOSE_LONG",
            price: sellPrice,
            profit: netProfit,
            timestamp: historicalData[i].timestamp,
            reason: "SIGNAL_CLOSE",
          });
          if (netProfit > 0) winningTrades++;
          totalTrades++;
        }

        // Open short position
        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        position = -(tradeAmount / sellPrice);
        const commissionCost = tradeAmount * commission;
        capital -= tradeAmount + commissionCost;

        trades.push({
          type: "OPEN_SHORT",
          price: sellPrice,
          amount: Math.abs(position),
          timestamp: historicalData[i].timestamp,
          commission: commissionCost,
        });
        totalTrades++;
      }

      // Track drawdown
      if (capital > peakCapital) {
        peakCapital = capital;
      }
      const drawdown = (peakCapital - capital) / peakCapital;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
    const totalReturn = ((capital - initialCapital) / initialCapital) * 100;
    const sharpeRatio = totalReturn / (maxDrawdown * 100 || 1);
    const profitFactor =
      winningTrades > 0 && totalTrades - winningTrades > 0
        ? winningTrades / (totalTrades - winningTrades)
        : 0;
    const avgWin =
      winningTrades > 0
        ? trades
          .filter((t) => t.profit > 0)
          .reduce((sum, t) => sum + t.profit, 0) / winningTrades
        : 0;
    // Calculate totalTrades from completed trades only (CLOSE actions)
    // This ensures it matches the Detailed Trade Report
    const completedTrades = trades.filter(
      (t) => t.type === "CLOSE_LONG" || t.type === "CLOSE_SHORT"
    );
    const actualTotalTrades = completedTrades.length;
    const actualWinningTrades = completedTrades.filter((t) => t.profit > 0).length;
    const actualWinRate = actualTotalTrades > 0 ? (actualWinningTrades / actualTotalTrades) * 100 : 0;
    const avgLoss = actualTotalTrades - actualWinningTrades > 0
      ? completedTrades
        .filter((t) => t.profit < 0)
        .reduce((sum, t) => sum + Math.abs(t.profit), 0) /
      (actualTotalTrades - actualWinningTrades)
      : 0;
    const maxConsecutiveWins = calculateMaxConsecutiveWins(completedTrades);
    const maxConsecutiveLosses = calculateMaxConsecutiveLosses(completedTrades);
    const totalCommission = trades.reduce(
      (sum, t) => sum + (t.commission || 0),
      0
    );
    const netProfit = totalProfit - totalCommission;

    return {
      totalTrades: actualTotalTrades,
      winningTrades: actualWinningTrades,
      winRate: actualWinRate,
      totalReturn,
      finalCapital: capital,
      maxDrawdown: maxDrawdown * 100,
      sharpeRatio,
      profitFactor: actualWinningTrades > 0 && actualTotalTrades - actualWinningTrades > 0
        ? actualWinningTrades / (actualTotalTrades - actualWinningTrades)
        : 0,
      avgWin: actualWinningTrades > 0
        ? completedTrades
          .filter((t) => t.profit > 0)
          .reduce((sum, t) => sum + t.profit, 0) / actualWinningTrades
        : 0,
      avgLoss,
      maxConsecutiveWins,
      maxConsecutiveLosses,
      totalCommission,
      netProfit,
      trades,
      strategy: selectedStrategy,
      symbol: selectedSymbol,
      timeframe,
      period: `${startDate} to ${endDate}`,
      initialCapital,
      advancedSettings,
    };
  };

  // Generate detailed trade-by-trade report
  const generateTradeReport = () => {
    if (!backtestResults || !backtestResults.trades) return [];

    const tradeReport = [];
    const trades = backtestResults.trades;

    let currentTrade = null;

    for (let i = 0; i < trades.length; i++) {
      const trade = trades[i];

      if (trade.type === "OPEN_LONG" || trade.type === "OPEN_SHORT") {
        currentTrade = {
          tradeNumber: tradeReport.length + 1,
          type: trade.type === "OPEN_LONG" ? "LONG" : "SHORT",
          entryTime: new Date(trade.timestamp).toLocaleString(),
          entryTimestamp: trade.timestamp, // Store original timestamp for duration calculation
          entryPrice: trade.price.toFixed(2),
          quantity: trade.amount || 0,
          exitTime: "",
          exitPrice: "",
          profit: 0,
          profitPercent: 0,
          exitReason: "",
          duration: "",
        };
      } else if (trade.type === "CLOSE_LONG" || trade.type === "CLOSE_SHORT") {
        if (currentTrade) {
          const entryPrice = parseFloat(currentTrade.entryPrice);
          const exitPrice = trade.price;
          const quantity = currentTrade.quantity;

          // Use profit from trade object (already calculated with commissions/slippage)
          const profit = trade.profit || 0;
          const profitPercent = quantity > 0 && entryPrice > 0
            ? (profit / (entryPrice * quantity)) * 100
            : 0;

          // Calculate duration using original timestamps
          const entryTime = new Date(currentTrade.entryTimestamp);
          const exitTime = new Date(trade.timestamp);
          const durationMs = exitTime.getTime() - entryTime.getTime();

          // Handle invalid dates
          let durationStr = "N/A";
          if (!isNaN(durationMs) && durationMs >= 0) {
            const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
            const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
            durationStr = `${durationHours}h ${durationMinutes}m`;
          }

          currentTrade.exitTime = exitTime.toLocaleString();
          currentTrade.exitPrice = exitPrice.toFixed(2);
          currentTrade.profit = profit;
          currentTrade.profitPercent = profitPercent;
          currentTrade.exitReason = trade.reason || "SIGNAL_CLOSE";
          currentTrade.duration = durationStr;

          tradeReport.push(currentTrade);
          currentTrade = null;
        }
      }
    }

    return tradeReport;
  };

  const exportResults = () => {
    if (!backtestResults) return;

    const tradeReport = generateTradeReport();

    const csvContent = [
      ["Strategy", backtestResults.strategy],
      ["Symbol", backtestResults.symbol],
      ["Timeframe", backtestResults.timeframe],
      ["Period", backtestResults.period],
      ["Initial Capital", backtestResults.initialCapital],
      ["Final Capital", backtestResults.finalCapital],
      ["Total Return (%)", backtestResults.totalReturn.toFixed(2)],
      ["Total Trades", backtestResults.totalTrades],
      ["Winning Trades", backtestResults.winningTrades],
      ["Win Rate (%)", backtestResults.winRate.toFixed(2)],
      ["Max Drawdown (%)", backtestResults.maxDrawdown.toFixed(2)],
      ["", ""],
      ["Trade Report", ""],
      ["Trade #", "Type", "Entry Time", "Entry Price", "Exit Time", "Exit Price", "Profit", "Profit %", "Exit Reason", "Duration"],
      ...tradeReport.map(t => [
        t.tradeNumber,
        t.type,
        t.entryTime,
        t.entryPrice,
        t.exitTime,
        t.exitPrice,
        t.profit.toFixed(2),
        t.profitPercent.toFixed(2),
        t.exitReason,
        t.duration
      ])
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `backtest-results-${selectedSymbol}-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Content
      Page_title="Backtesting & Strategy Simulation"
      button_status={false}
    >
      <div className="backtesting-container">
   

        {/* Configuration Section - Step 1 & 2 */}
        <Row className="mb-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-primary d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center">
                  <Settings size={18} className="me-2" />
                  Step 1 & 2: Strategy & Settings
                </h5>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setShowSettings(!showSettings)}
                >
                  {showSettings ? "Hide" : "Show"} Advanced Settings
                </Button>
              </Card.Header>
              <Card.Body>
                {/* Basic Settings - Always Visible */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <Target size={14} className="me-1" />
                        Strategy <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Select
                          value={selectedStrategy}
                          onChange={(e) => setSelectedStrategy(e.target.value)}
                          className="flex-grow-1"
                          isInvalid={!selectedStrategy}
                        >
                          <option value="">-- Select Strategy --</option>
                          {strategies.map((strategy) => (
                            <option key={strategy.id} value={strategy.id}>
                              {strategy.name}
                            </option>
                          ))}
                        </Form.Select>
                        {selectedStrategy && (
                          <Button variant="outline-info" size="sm" onClick={() => setShowDocumentation(true)} title="View Documentation">
                            <i className="fas fa-book"></i>
                          </Button>
                        )}
                      </div>
                      {!selectedStrategy && (
                        <Form.Text className="text-danger small">
                          <i className="fas fa-exclamation-circle me-1"></i>Required
                        </Form.Text>
                      )}
                      {selectedStrategy && (
                        <div className="mt-2">
                          <Badge bg={strategies.find((s) => s.id === selectedStrategy)?.documentation?.riskLevel === "Low" ? "success" : strategies.find((s) => s.id === selectedStrategy)?.documentation?.riskLevel === "Medium" ? "warning" : "danger"}>
                            <Shield size={12} className="me-1" />
                            Risk: {strategies.find((s) => s.id === selectedStrategy)?.documentation?.riskLevel}
                          </Badge>
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <DollarSign size={14} className="me-1" />
                        Symbol
                      </Form.Label>
                      <Form.Select value={selectedSymbol} onChange={(e) => setSelectedSymbol(e.target.value)}>
                        {symbols.map((symbol) => (
                          <option key={symbol} value={symbol}>{symbol}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <Clock size={14} className="me-1" />
                        Timeframe
                      </Form.Label>
                      <Form.Select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                        {timeframes.map((tf) => (
                          <option key={tf.value} value={tf.value}>{tf.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <Calendar size={14} className="me-1" />
                        Start Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        max={endDate || new Date().toISOString().split('T')[0]}
                        min={(() => {
                          const maxDate = new Date();
                          maxDate.setFullYear(maxDate.getFullYear() - 5);
                          return maxDate.toISOString().split('T')[0];
                        })()}
                        isInvalid={!startDate}
                      />
                      {!startDate && (
                        <Form.Text className="text-danger small">
                          <i className="fas fa-exclamation-circle me-1"></i>Required
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <Calendar size={14} className="me-1" />
                        End Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        max={new Date().toISOString().split('T')[0]}
                        isInvalid={!endDate || (startDate && endDate < startDate)}
                      />
                      {(!endDate || (startDate && endDate < startDate)) && (
                        <Form.Text className="text-danger small">
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {!endDate ? "Required" : "Invalid date"}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <DollarSign size={14} className="me-1" />
                        Capital ($)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={initialCapital}
                        onChange={(e) => setInitialCapital(parseFloat(e.target.value))}
                        min="1000"
                        step="1000"
                        isInvalid={initialCapital < 1000}
                      />
                      {initialCapital < 1000 && (
                        <Form.Text className="text-danger small">Min: $1,000</Form.Text>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex align-items-center">
                        <Shield size={14} className="me-1" />
                        Risk (%)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={riskPerTrade}
                        onChange={(e) => setRiskPerTrade(parseFloat(e.target.value))}
                        min="0.1"
                        max="10"
                        step="0.1"
                        isInvalid={riskPerTrade < 0.1 || riskPerTrade > 10}
                      />
                      <Form.Text className={riskPerTrade >= 1 && riskPerTrade <= 2 ? "text-success small" : "text-warning small"}>
                        {riskPerTrade}%
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Advanced Settings - Collapsible */}
                {showSettings && (
                  <div className="mt-4 pt-4 border-top">
                    {/* Strategy Parameters */}
                    {selectedStrategy &&
                      selectedStrategy !== "strategy_builder" &&
                      selectedStrategy !== "custom_strategy" && (
                        <div className="mb-4">
                          {renderStrategyParameters(selectedStrategy)}
                        </div>
                      )}

                    {/* Risk & Trading Settings - Grouped */}
                    <Row className="mb-3">
                      <Col md={6}>
                        <div className="p-3 border rounded bg-light h-100">
                          <h6 className="mb-3 d-flex align-items-center">
                            <Shield size={14} className="me-2" />
                            Risk Management
                          </h6>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">Stop Loss (%)</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={advancedSettings.stopLoss}
                                  onChange={(e) =>
                                    setAdvancedSettings((prev) => ({
                                      ...prev,
                                      stopLoss: parseFloat(e.target.value),
                                    }))
                                  }
                                  min="0.1"
                                  max="20"
                                  step="0.1"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">Take Profit (%)</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={advancedSettings.takeProfit}
                                  onChange={(e) =>
                                    setAdvancedSettings((prev) => ({
                                      ...prev,
                                      takeProfit: parseFloat(e.target.value),
                                    }))
                                  }
                                  min="0.1"
                                  max="50"
                                  step="0.1"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="p-3 border rounded bg-light h-100">
                          <h6 className="mb-3 d-flex align-items-center">
                            <DollarSign size={14} className="me-2" />
                            Trading Costs
                          </h6>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">Commission (%)</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={advancedSettings.commission}
                                  onChange={(e) =>
                                    setAdvancedSettings((prev) => ({
                                      ...prev,
                                      commission: parseFloat(e.target.value),
                                    }))
                                  }
                                  min="0"
                                  max="1"
                                  step="0.01"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">Slippage (%)</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  value={advancedSettings.slippage}
                                  onChange={(e) =>
                                    setAdvancedSettings((prev) => ({
                                      ...prev,
                                      slippage: parseFloat(e.target.value),
                                    }))
                                  }
                                  min="0"
                                  max="1"
                                  step="0.01"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>

                    {/* Trading Configuration - Compact */}
                    <div className="mb-3 p-3 border rounded bg-light">
                      <h6 className="mb-3 d-flex align-items-center">
                        <Activity size={14} className="me-2" />
                        Trading Configuration
                      </h6>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Leverage</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={advancedSettings.leverage}
                              onChange={(e) =>
                                setAdvancedSettings((prev) => ({
                                  ...prev,
                                  leverage: parseFloat(e.target.value),
                                }))
                              }
                              min="1"
                              max="100"
                              step="0.1"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Max Trades</Form.Label>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={advancedSettings.maxTrades}
                              onChange={(e) =>
                                setAdvancedSettings((prev) => ({
                                  ...prev,
                                  maxTrades: parseInt(e.target.value),
                                }))
                              }
                              min="10"
                              max="10000"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Trading Type</Form.Label>
                            <Form.Select
                              size="sm"
                              value={advancedSettings.strategyType}
                              onChange={(e) =>
                                setAdvancedSettings((prev) => ({
                                  ...prev,
                                  strategyType: e.target.value,
                                }))
                              }
                            >
                              <option value="both">Both</option>
                              <option value="long">Long Only</option>
                              <option value="short">Short Only</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label className="small">Trading Permissions</Form.Label>
                            <div className="d-flex gap-3">
                              <Form.Check
                                type="switch"
                                id="allowLong"
                                checked={advancedSettings.allowLongTrading}
                                onChange={(e) =>
                                  setAdvancedSettings((prev) => ({
                                    ...prev,
                                    allowLongTrading: e.target.checked,
                                  }))
                                }
                                label="Long"
                              />
                              <Form.Check
                                type="switch"
                                id="allowShort"
                                checked={advancedSettings.allowShortSelling}
                                onChange={(e) =>
                                  setAdvancedSettings((prev) => ({
                                    ...prev,
                                    allowShortSelling: e.target.checked,
                                  }))
                                }
                                label="Short"
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Custom Strategy Code */}
                    {selectedStrategy === "custom_strategy" && (
                      <div className="mb-3 p-3 border rounded bg-light">
                        <h6 className="mb-3 d-flex align-items-center">
                          <Target size={14} className="me-2" />
                          Custom Strategy Code
                        </h6>
                        <Form.Group className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Strategy Code</Form.Label>
                            <Form.Select
                              value={advancedSettings.customStrategyLanguage}
                              onChange={(e) =>
                                setAdvancedSettings((prev) => ({
                                  ...prev,
                                  customStrategyLanguage: e.target.value,
                                }))
                              }
                              size="sm"
                              style={{ width: 'auto' }}
                            >
                              <option value="javascript">JavaScript</option>
                              <option value="python">Python</option>
                            </Form.Select>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={8}
                            value={advancedSettings.customStrategy}
                            onChange={(e) =>
                              setAdvancedSettings((prev) => ({
                                ...prev,
                                customStrategy: e.target.value,
                              }))
                            }
                            placeholder={
                              advancedSettings.customStrategyLanguage === "javascript"
                                ? `// JavaScript Strategy\nfunction customStrategy(prices, volumes, index) {\n  if (index < 2) return 'HOLD';\n  const priceChange = (prices[index] - prices[index-1]) / prices[index-1];\n  if (priceChange > 0.005) return 'BUY';\n  if (priceChange < -0.005) return 'SELL';\n  return 'HOLD';\n}`
                                : `# Python Strategy\ndef custom_strategy(prices, volumes, index):\n    if index < 2: return 'HOLD'\n    price_change = (prices[index] - prices[index-1]) / prices[index-1]\n    if price_change > 0.005: return 'BUY'\n    if price_change < -0.005: return 'SELL'\n    return 'HOLD'`
                            }
                          />
                          <Form.Text className="text-muted small">
                            Return 'BUY', 'SELL', or 'HOLD'
                          </Form.Text>
                        </Form.Group>
                      </div>
                    )}

                    {/* Strategy Builder */}
                    {selectedStrategy === "strategy_builder" && (
                      <div className="mb-3 p-3 border rounded bg-light">
                        <h6 className="mb-3 d-flex align-items-center">
                          <Target size={14} className="me-2" />
                          Strategy Builder
                        </h6>
                        <Alert variant="info" className="mb-3 py-2">
                          <small>Build your strategy using conditions and actions. Use Custom Strategy for code-based approach.</small>
                        </Alert>
                        <div className="border rounded p-3">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Conditions</h6>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                const newCondition = {
                                  id: Date.now(),
                                  type: "indicator",
                                  indicator: "sma",
                                  period: 20,
                                  operator: ">",
                                  value: 0,
                                  condition: "price",
                                };
                                setStrategyBuilder((prev) => ({
                                  ...prev,
                                  conditions: [...prev.conditions, newCondition],
                                }));
                              }}
                            >
                              <i className="fas fa-plus me-1"></i> Add
                            </Button>
                          </div>
                          {strategyBuilder.conditions.map((condition, index) => (
                            <div key={condition.id} className="mb-2 p-2 border rounded bg-white">
                              <Row className="align-items-center g-2">
                                <Col md={3}>
                                  <Form.Select
                                    size="sm"
                                    value={condition.type}
                                    onChange={(e) => {
                                      const newConditions = [...strategyBuilder.conditions];
                                      newConditions[index].type = e.target.value;
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        conditions: newConditions,
                                      }));
                                    }}
                                  >
                                    <option value="indicator">Indicator</option>
                                    <option value="price">Price</option>
                                    <option value="volume">Volume</option>
                                  </Form.Select>
                                </Col>
                                {condition.type === "indicator" && (
                                  <>
                                    <Col md={2}>
                                      <Form.Select
                                        size="sm"
                                        value={condition.indicator}
                                        onChange={(e) => {
                                          const newConditions = [...strategyBuilder.conditions];
                                          newConditions[index].indicator = e.target.value;
                                          setStrategyBuilder((prev) => ({
                                            ...prev,
                                            conditions: newConditions,
                                          }));
                                        }}
                                      >
                                        <option value="sma">SMA</option>
                                        <option value="ema">EMA</option>
                                        <option value="rsi">RSI</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={2}>
                                      <Form.Control
                                        size="sm"
                                        type="number"
                                        value={condition.period}
                                        onChange={(e) => {
                                          const newConditions = [...strategyBuilder.conditions];
                                          newConditions[index].period = parseInt(e.target.value);
                                          setStrategyBuilder((prev) => ({
                                            ...prev,
                                            conditions: newConditions,
                                          }));
                                        }}
                                        placeholder="20"
                                      />
                                    </Col>
                                  </>
                                )}
                                <Col md={2}>
                                  <Form.Select
                                    size="sm"
                                    value={condition.operator}
                                    onChange={(e) => {
                                      const newConditions = [...strategyBuilder.conditions];
                                      newConditions[index].operator = e.target.value;
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        conditions: newConditions,
                                      }));
                                    }}
                                  >
                                    <option value=">">&gt;</option>
                                    <option value="<">&lt;</option>
                                    <option value=">=">&gt;=</option>
                                    <option value="<=">&lt;=</option>
                                    <option value="==">=</option>
                                  </Form.Select>
                                </Col>
                                <Col md={2}>
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={condition.value}
                                    onChange={(e) => {
                                      const newConditions = [...strategyBuilder.conditions];
                                      newConditions[index].value = parseFloat(e.target.value);
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        conditions: newConditions,
                                      }));
                                    }}
                                    placeholder="0"
                                  />
                                </Col>
                                <Col md={1}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        conditions: prev.conditions.filter((_, i) => i !== index),
                                      }));
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <hr className="my-3" />

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Actions</h6>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                const newAction = {
                                  id: Date.now(),
                                  condition: "all",
                                  signal: "BUY",
                                  description: "Buy when all conditions are met",
                                };
                                setStrategyBuilder((prev) => ({
                                  ...prev,
                                  actions: [...prev.actions, newAction],
                                }));
                              }}
                            >
                              <i className="fas fa-plus me-1"></i> Add
                            </Button>
                          </div>
                          {strategyBuilder.actions.map((action, index) => (
                            <div key={action.id} className="mb-2 p-2 border rounded bg-white">
                              <Row className="align-items-center g-2">
                                <Col md={3}>
                                  <Form.Select
                                    size="sm"
                                    value={action.condition}
                                    onChange={(e) => {
                                      const newActions = [...strategyBuilder.actions];
                                      newActions[index].condition = e.target.value;
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        actions: newActions,
                                      }));
                                    }}
                                  >
                                    <option value="all">All conditions</option>
                                    <option value="any">Any condition</option>
                                  </Form.Select>
                                </Col>
                                <Col md={3}>
                                  <Form.Select
                                    size="sm"
                                    value={action.signal}
                                    onChange={(e) => {
                                      const newActions = [...strategyBuilder.actions];
                                      newActions[index].signal = e.target.value;
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        actions: newActions,
                                      }));
                                    }}
                                  >
                                    <option value="BUY">BUY</option>
                                    <option value="SELL">SELL</option>
                                    <option value="HOLD">HOLD</option>
                                  </Form.Select>
                                </Col>
                                <Col md={5}>
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={action.description}
                                    onChange={(e) => {
                                      const newActions = [...strategyBuilder.actions];
                                      newActions[index].description = e.target.value;
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        actions: newActions,
                                      }));
                                    }}
                                    placeholder="Description"
                                  />
                                </Col>
                                <Col md={1}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      setStrategyBuilder((prev) => ({
                                        ...prev,
                                        actions: prev.actions.filter((_, i) => i !== index),
                                      }));
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <div className="mt-3 d-flex gap-2">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                const generatedCode = generateStrategyCode();
                                setAdvancedSettings((prev) => ({
                                  ...prev,
                                  customStrategy: generatedCode,
                                }));
                                alert("Strategy code generated!");
                              }}
                            >
                              <i className="fas fa-code me-1"></i> Generate Code
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => {
                                try {
                                  const generatedCode = generateStrategyCode();
                                  const func = new Function("prices", "volumes", "index", generatedCode);
                                  const testResult = func([100, 101, 102, 103, 104], [1000, 1100, 1200, 1300, 1400], 4);
                                  alert(`Test result: ${testResult}`);
                                } catch (error) {
                                  alert(`Error: ${error.message}`);
                                }
                              }}
                            >
                              <i className="fas fa-play me-1"></i> Test
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3 & 4: Actions */}
                <div className="step-actions-container p-3 bg-light rounded mt-4 mb-3">
                <h6 className="mb-3 d-flex align-items-center">
                    <PlayCircle size={16} className="me-2" />
                    Step 3 & 4: Fetch Data & Run Backtest
                  </h6>

                  {/* Validation Status */}
                  {(!selectedStrategy || !startDate || !endDate) && (
                    <Alert variant="warning" className="mb-3 py-2">
                      <small>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Required:</strong> Please select Strategy, Start Date, and End Date before fetching data.
                      </small>
                    </Alert>
                  )}

                  {selectedStrategy && startDate && endDate && !historicalData.length && (
                    <Alert variant="info" className="mb-3 py-2">
                      <small>
                        <i className="fas fa-info-circle me-2"></i>
                        Ready to fetch data! Click "Fetch Historical Data" to continue.
                      </small>
                    </Alert>
                  )}

                  {historicalData.length > 0 && !backtestResults && (
                    <Alert variant="success" className="mb-3 py-2">
                      <small>
                        <i className="fas fa-check-circle me-2"></i>
                        <strong>Data loaded:</strong> {historicalData.length} candles ready. Click "Run Backtest" to start testing.
                      </small>
                    </Alert>
                  )}

                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={fetchHistoricalData}
                      disabled={isLoading || !selectedStrategy || !startDate || !endDate}
                      className="d-flex align-items-center gap-2"
                      title={!selectedStrategy ? "Please select a strategy first" : !startDate || !endDate ? "Please set start and end dates" : "Fetch historical data for backtesting"}
                    >
                      <Download size={14} />
                      Step 3: Fetch Historical Data
                      {historicalData.length > 0 && (
                        <Badge bg="success" className="ms-1">{historicalData.length}</Badge>
                      )}
                    </Button>

                    <Button
                      variant="success"
                      size="sm"
                      onClick={runBacktest}
                      disabled={
                        isLoading || !selectedStrategy || !historicalData.length
                      }
                      className="d-flex align-items-center gap-2"
                      title={!selectedStrategy ? "Please select a strategy first" : !historicalData.length ? "Please fetch data first" : "Run backtest with selected strategy"}
                    >
                      <Calculator size={14} />
                      Step 4: Run Backtest
                      {backtestResults && (
                        <Badge className="ms-1">
                          <i className="fas fa-check me-1"></i>Done
                        </Badge>
                      )}
                    </Button>

                  </div>

                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {backtestResults && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={exportResults}
                        className="d-flex align-items-center gap-2"
                      >
                        <Download size={14} />
                        Export Report
                      </Button>
                    )}

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={clearAllData}
                      className="d-flex align-items-center gap-2"
                      title="Clear all backtest data"
                    >
                      <RotateCcw size={14} />
                      Clear Data
                    </Button>

                    {historicalData.length > 0 && (
                      <>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => setShowChart(!showChart)}
                          className="d-flex align-items-center gap-2"
                        >
                          <BarChart3 size={14} />
                          {showChart ? "Hide" : "Show"} Line Chart
                        </Button>

                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() =>
                            setShowCandlestickChart(!showCandlestickChart)
                          }
                          className="d-flex align-items-center gap-2"
                        >
                          <BarChart3 size={14} />
                          {showCandlestickChart ? "Hide" : "Show"} Candlestick
                        </Button>
                      </>
                    )}
                  </div>
              </div>

              {isLoading && (
                <div className="mt-3">
                  <ProgressBar now={progress} label={`${progress}%`} />
                  <small className="text-muted">Processing backtest...</small>
                </div>
              )}
            </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Quick Stats & Progress */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-primary">
                <h5 className="mb-0 d-flex align-items-center">
                  <Activity size={18} className="me-2" />
                  Status & Progress
                </h5>
              </Card.Header>
              <Card.Body>
                {/* Status Indicators */}
                <div className="mb-3">
                  {!selectedStrategy ? (
                    <Alert variant="warning" className="py-2 mb-2">
                      <small><i className="fas fa-exclamation-circle me-1"></i> No strategy selected</small>
                    </Alert>
                  ) : (
                    <Alert variant="success" className="py-2 mb-2">
                      <small><i className="fas fa-check-circle me-1"></i> Strategy: {strategies.find(s => s.id === selectedStrategy)?.name}</small>
                    </Alert>
                  )}

                  {!startDate || !endDate ? (
                    <Alert variant="warning" className="py-2 mb-2">
                      <small><i className="fas fa-calendar-times me-1"></i> Date range not set</small>
                    </Alert>
                  ) : (
                    <Alert variant="info" className="py-2 mb-2">
                      <small><i className="fas fa-calendar-check me-1"></i> Date range set</small>
                    </Alert>
                  )}

                  {historicalData.length === 0 ? (
                    <Alert variant="secondary" className="py-2 mb-0">
                      <small><i className="fas fa-database me-1"></i> No data loaded</small>
                    </Alert>
                  ) : (
                    <Alert variant="success" className="py-2 mb-0">
                      <small><i className="fas fa-database me-1"></i> {historicalData.length} candles loaded</small>
                    </Alert>
                  )}
                </div>

                <hr />

                <div className="stats-grid">
                  <div className="stat-item mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="d-flex align-items-center">
                        <Activity size={14} className="me-1 text-muted" />
                        Data Points:
                      </span>
                      {/* <Badge > */}
                      {historicalData.length || "0"}
                      {/* </Badge> */}
                    </div>
                  </div>

                  <div className="stat-item mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="d-flex align-items-center">
                        <Calendar size={14} className="me-1 text-muted" />
                        Period:
                      </span>
                      <small >
                        {startDate && endDate
                          ? `${Math.ceil(
                            (new Date(endDate) - new Date(startDate)) /
                            (1000 * 60 * 60 * 24)
                          )} days`
                          : "Not set"}
                      </small>
                    </div>
                  </div>

                  <div className="stat-item mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="d-flex align-items-center">
                        <Clock size={14} className="me-1 text-muted" />
                        Timeframe:
                      </span>
                      {timeframe}
                    </div>
                  </div>

                  <div className="stat-item mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="d-flex align-items-center">
                        <DollarSign size={14} className="me-1 text-muted" />
                        Symbol:
                      </span>
                      {selectedSymbol}
                    </div>
                  </div>

                  {backtestResults && (
                    <div className="stat-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center">
                          <TrendingUp size={14} className="me-1 text-muted" />
                          Total Trades:
                        </span>
                        {backtestResults.totalTrades || 0}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>

            {/* Quick Tips */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-primary">
                <h5 className="mb-0 d-flex align-items-center">
                  <Zap size={18} className="me-2" />
                  Quick Tips
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="tips-list">
                  <div className="tip-item mb-2">
                    <div className="d-flex align-items-start">
                      <Target size={14} className=" me-2 mt-1" />
                      <small>
                        Use at least 3 months of data for reliable results. You can backtest up to 5 years of historical data.
                      </small>
                    </div>
                  </div>

                  <div className="tip-item mb-2">
                    <div className="d-flex align-items-start">
                      <Shield size={14} className=" me-2 mt-1" />
                      <small>Keep risk per trade under 2%</small>
                    </div>
                  </div>

                  <div className="tip-item mb-2">
                    <div className="d-flex align-items-start">
                      <TrendingUp
                        size={14}
                        className="text-success me-2 mt-1"
                      />
                      <small>Look for win rate above 50%</small>
                    </div>
                  </div>

                  <div className="tip-item">
                    <div className="d-flex align-items-start">
                      <Calculator size={14} className="text-info me-2 mt-1" />
                      <small>Sharpe ratio above 1.0 is good</small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Candlestick Chart Section */}
        {showCandlestickChart && historicalData.length > 0 && (
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-primary  d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <BarChart3 size={20} className="me-2" />
                    Live Candlestick Chart - {selectedSymbol} ({timeframe})
                  </h5>
                  <div className="d-flex gap-2">
                    <Badge bg="primary">{historicalData.length} Candles</Badge>
                    {isBacktestRunning && (
                      <Badge bg="primary">
                        Processing: {currentBacktestIndex}/
                        {historicalData.length}
                      </Badge>
                    )}
                    {backtestResults && (
                      <Badge bg="primary">
                        {backtestResults.totalTrades} Trades Executed
                      </Badge>
                    )}
                    {selectedStrategy &&
                      historicalData.length > 0 &&
                      (() => {
                        const testData = historicalData
                          .map((candle) => ({
                            time: Math.floor(
                              new Date(candle.timestamp).getTime() / 1000
                            ),
                            open: parseFloat(candle.open),
                            high: parseFloat(candle.high),
                            low: parseFloat(candle.low),
                            close: parseFloat(candle.close),
                          }))
                          .sort((a, b) => a.time - b.time);
                        const signals = generateStrategySignals(testData);
                        return (
                          <Badge bg="primary">
                            {signals.length} Signals Available
                          </Badge>
                        );
                      })()}
                  </div>
                </Card.Header>
                <Card.Body>
                  

                  {/* Animation Controls */}
                  {isBacktestRunning && (
                    <div className="mb-3 p-3 border rounded ">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <Form.Group className="mb-0">
                            <Form.Label className="small mb-1">
                              Animation Speed
                            </Form.Label>
                            <Form.Range
                              min="5"
                              max="100"
                              value={animationSpeed}
                              onChange={(e) =>
                                setAnimationSpeed(parseInt(e.target.value))
                              }
                              className="mb-1"
                            />
                            <small className="text-muted">
                              {animationSpeed}ms per candle •
                              {animationSpeed <= 10
                                ? " Very Fast"
                                : animationSpeed <= 30
                                  ? " Fast"
                                  : animationSpeed <= 50
                                    ? " Medium"
                                    : " Slow"}
                            </small>
                          </Form.Group>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setIsBacktestRunning(false)}
                            >
                              <i className="fas fa-stop me-1"></i>
                              Stop Animation
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() =>
                                setAnimationSpeed(animationSpeed === 5 ? 20 : 5)
                              }
                            >
                              {animationSpeed === 5
                                ? "Normal Speed"
                                : "Turbo Mode"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                

                  {/* Strategy Signal Testing Controls */}
                  {selectedStrategy && historicalData.length > 0 && (
                    <div className="mb-4">
                      <div className="p-3 chart-testing-controls">
                        <h6 className="mb-3">
                          <i className="fas fa-chart-line me-2"></i>
                          Strategy Signal Testing
                        </h6>
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="testing-button"
                            onClick={runTestAnimationWithSignals}
                            disabled={!selectedStrategy}
                          >
                            <i className="fas fa-chart-line me-1"></i>
                            Animated Signals
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="testing-button"
                            onClick={showAllStrategySignals}
                            disabled={!selectedStrategy}
                          >
                            <i className="fas fa-eye me-1"></i>
                            Show All Signals
                          </Button>
                          {!selectedStrategy && (
                            <small className="text-muted">
                              <i className="fas fa-info-circle me-1"></i>
                              Select a strategy to enable signal testing
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    ref={chartContainerRef}
                    style={{
                      height: "600px",
                      width: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  />

                  {/* Chart Controls */}
                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          if (chartRef.current) {
                            const canvas = chartRef.current.takeScreenshot();
                            const url = canvas.toDataURL("image/png");
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${selectedSymbol}-candlestick-${Date.now()}.png`;
                            a.click();
                          }
                        }}
                      >
                        <Download size={14} className="me-1" />
                        Export Candlestick Chart
                      </Button>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          if (chartRef.current) {
                            chartRef.current.timeScale().fitContent();
                          }
                        }}
                      >
                        <i className="fas fa-expand-arrows-alt me-1"></i>
                        Fit Content
                      </Button>
                    </div>

                    <div className="text-muted small">
                      Chart shows {historicalData.length} candles from{" "}
                      {startDate} to {endDate}
                      {isBacktestRunning && (
                        <span className="ms-2 text-info">
                          • Currently processing candle {currentBacktestIndex}
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Line Chart Section */}
        {showChart && chartData && (
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-info  d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <BarChart3 size={20} className="me-2" />
                    Price Chart - {selectedSymbol} ({timeframe})
                  </h5>
                  <div className="d-flex gap-2">
                    <Badge >
                      {historicalData.length} Data Points
                    </Badge>
                    {backtestResults && (
                      <Badge bg="success">
                        {backtestResults.totalTrades} Trades
                      </Badge>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <div style={{ height: "500px", position: "relative" }}>
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          intersect: false,
                          mode: "index",
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: `${selectedSymbol} Price Chart with ${selectedStrategy
                              ? strategies.find(
                                (s) => s.id === selectedStrategy
                              )?.name
                              : "No Strategy"
                              }`,
                          },
                          legend: {
                            display: true,
                            position: "top",
                          },
                          tooltip: {
                            callbacks: {
                              title: function (context) {
                                return new Date(
                                  chartData.labels[context[0].dataIndex]
                                ).toLocaleString();
                              },
                              label: function (context) {
                                if (context.datasetIndex === 0) {
                                  return `Price: $${context.parsed.y.toFixed(
                                    2
                                  )}`;
                                } else {
                                  return `Volume: ${context.parsed.y.toLocaleString()}`;
                                }
                              },
                            },
                          },
                        },
                        scales: {
                          x: {
                            display: true,
                            title: {
                              display: true,
                              text: "Time",
                            },
                          },
                          y: {
                            type: "linear",
                            display: true,
                            position: "left",
                            title: {
                              display: true,
                              text: "Price ($)",
                            },
                          },
                          y1: {
                            type: "linear",
                            display: true,
                            position: "right",
                            title: {
                              display: true,
                              text: "Volume",
                            },
                            grid: {
                              drawOnChartArea: false,
                            },
                          },
                        },
                      }}
                    />
                  </div>

                  {/* Chart Controls */}
                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          const canvas = document.querySelector("canvas");
                          if (canvas) {
                            const url = canvas.toDataURL("image/png");
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${selectedSymbol}-chart-${Date.now()}.png`;
                            a.click();
                          }
                        }}
                      >
                        <Download size={14} className="me-1" />
                        Export Chart
                      </Button>
                    </div>

                    <div className="text-muted small">
                      Chart shows {historicalData.length} data points from{" "}
                      {startDate} to {endDate}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Results Section - Step 5 */}
        {backtestResults && (
          <Row className="mb-4">
            <Col lg={8}>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Header className="bg-success">
                  <h5 className="mb-0 d-flex align-items-center">
                    <BarChart3 size={18} className="me-2" />
                    Step 5: Backtest Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="result-card p-3 border rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">Total Return:</span>
                          <Badge
                            bg={
                              backtestResults.totalReturn >= 0
                                ? "success"
                                : "danger"
                            }
                            className="fs-6"
                          >
                            {backtestResults.totalReturn >= 0 ? "+" : ""}
                            {backtestResults.totalReturn.toFixed(2)}%
                          </Badge>
                        </div>
                        <small className="text-muted">
                          ${backtestResults.initialCapital.toFixed(2)} → $
                          {backtestResults.finalCapital.toFixed(2)}
                        </small>
                      </div>

                      <div className="result-card p-3 border rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">Win Rate:</span>
                     
                            {backtestResults.winRate.toFixed(1)}%
                          
                        </div>
                        <small className="text-muted">
                          {backtestResults.winningTrades} /{" "}
                          {backtestResults.totalTrades} trades
                        </small>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="result-card p-3 border rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">Max Drawdown:</span>

                          {backtestResults.maxDrawdown.toFixed(2)}%

                        </div>
                        <small className="text-muted">
                          Maximum loss from peak
                        </small>
                      </div>

                      <div className="result-card p-3 border rounded mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">Sharpe Ratio:</span>

                          {backtestResults.sharpeRatio.toFixed(2)}

                        </div>
                        <small className="text-muted">
                          Risk-adjusted return
                        </small>
                      </div>
                    </Col>
                  </Row>

                  <div className="mt-4">
                    <h6>Strategy Performance Summary</h6>
                    <Alert variant="info">
                      <strong>
                        {
                          strategies.find(
                            (s) => s.id === backtestResults.strategy
                          )?.name
                        }
                      </strong>{" "}
                      on {backtestResults.symbol}({backtestResults.timeframe})
                      from {backtestResults.period}. Generated{" "}
                      {backtestResults.totalTrades} trades with{" "}
                      {backtestResults.winRate.toFixed(1)}% win rate.
                    </Alert>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-primary ">
                  <h5 className="mb-0 d-flex align-items-center">
                    <TrendingUp size={20} className="me-2" />
                    Performance Metrics
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="metrics-list">
                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Total Trades:</span>
                        <strong>{backtestResults.totalTrades}</strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Winning Trades:</span>
                        <strong className="text-success">
                          {backtestResults.winningTrades}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Losing Trades:</span>
                        <strong className="text-danger">
                          {backtestResults.totalTrades -
                            backtestResults.winningTrades}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Average Trade:</span>
                        <strong>
                          {(
                            (backtestResults.finalCapital -
                              backtestResults.initialCapital) /
                            backtestResults.totalTrades
                          ).toFixed(2)}
                          $
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Profit Factor:</span>
                        <strong>
                          {backtestResults.profitFactor
                            ? backtestResults.profitFactor.toFixed(2)
                            : "N/A"}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Avg Win:</span>
                        <strong className="text-success">
                          $
                          {backtestResults.avgWin
                            ? backtestResults.avgWin.toFixed(2)
                            : "0.00"}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Avg Loss:</span>
                        <strong className="text-danger">
                          $
                          {backtestResults.avgLoss
                            ? backtestResults.avgLoss.toFixed(2)
                            : "0.00"}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Max Consecutive Wins:</span>
                        <strong className="text-success">
                          {backtestResults.maxConsecutiveWins || 0}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Max Consecutive Losses:</span>
                        <strong className="text-danger">
                          {backtestResults.maxConsecutiveLosses || 0}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Total Commission:</span>
                        <strong className="">
                          $
                          {backtestResults.totalCommission
                            ? backtestResults.totalCommission.toFixed(2)
                            : "0.00"}
                        </strong>
                      </div>
                    </div>

                    <div className="metric-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Net Profit:</span>
                        <strong
                          className={
                            backtestResults.netProfit >= 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          $
                          {backtestResults.netProfit
                            ? backtestResults.netProfit.toFixed(2)
                            : "0.00"}
                        </strong>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Detailed Trade Report */}
        {backtestResults && (
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-info d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 d-flex align-items-center">
                    <Activity size={18} className="me-2" />
                    Detailed Trade Report
                  </h5>

                  {generateTradeReport().length} Trades

                </Card.Header>
                <Card.Body>
                 

                  {generateTradeReport().length > 0 ? (
                    <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                      <Table striped bordered hover size="sm">
                        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Entry Time</th>
                            <th>Entry Price</th>
                            <th>Exit Time</th>
                            <th>Exit Price</th>
                            <th>Profit/Loss</th>
                            <th>Profit %</th>
                            <th>Duration</th>
                            <th>Exit Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generateTradeReport().map((trade, index) => (
                            <tr key={index}>
                              <td>{trade.tradeNumber}</td>
                              <td>
                                <Badge bg={trade.type === "LONG" ? "success" : "danger"}>
                                  {trade.type}
                                </Badge>
                              </td>
                              <td>{trade.entryTime}</td>
                              <td>${trade.entryPrice}</td>
                              <td>{trade.exitTime || "Open"}</td>
                              <td>${trade.exitPrice || "-"}</td>
                              <td>
                                <strong
                                  className={
                                    trade.profit >= 0 ? "text-success" : "text-danger"
                                  }
                                >
                                  ${trade.profit.toFixed(2)}
                                </strong>
                              </td>
                              <td>
                                <strong
                                  className={
                                    trade.profitPercent >= 0
                                      ? "text-success"
                                      : "text-danger"
                                  }
                                >
                                  {trade.profitPercent >= 0 ? "+" : ""}
                                  {trade.profitPercent.toFixed(2)}%
                                </strong>
                              </td>
                              <td>{trade.duration}</td>
                              <td>
                                <Badge
                                  bg={
                                    trade.exitReason === "TAKE_PROFIT"
                                      ? "success"
                                      : trade.exitReason === "STOP_LOSS"
                                        ? "danger"
                                        : "info"
                                  }
                                >
                                  {trade.exitReason === "TAKE_PROFIT"
                                    ? "Take Profit"
                                    : trade.exitReason === "STOP_LOSS"
                                      ? "Stop Loss"
                                      : "Signal"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="fw-bold">
                            <td colSpan="6" className="text-end">
                              Total Profit/Loss:
                            </td>
                            <td
                              className={
                                generateTradeReport().reduce(
                                  (sum, t) => sum + t.profit,
                                  0
                                ) >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              $
                              {generateTradeReport()
                                .reduce((sum, t) => sum + t.profit, 0)
                                .toFixed(2)}
                            </td>
                            <td
                              className={
                                generateTradeReport().reduce(
                                  (sum, t) => sum + t.profitPercent,
                                  0
                                ) >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {generateTradeReport().reduce(
                                (sum, t) => sum + t.profitPercent,
                                0
                              ) >= 0
                                ? "+"
                                : ""}
                              {generateTradeReport()
                                .reduce((sum, t) => sum + t.profitPercent, 0)
                                .toFixed(2)}%
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant="warning">
                      No completed trades found. Please run a backtest first.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Strategy Documentation Modal */}
        {selectedStrategy && renderStrategyDocumentation(selectedStrategy)}
      </div>

      <style jsx>{`
        .backtesting-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 15px;
        }

        .step-actions-container {
          border-left: 4px solid #007bff;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }

        .stats-grid .stat-item {
          padding: 0.75rem 0;
          border-bottom: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }

        .stats-grid .stat-item:last-child {
          border-bottom: none;
        }

        .stats-grid .stat-item:hover {
          background-color: #f8f9fa;
          padding-left: 8px;
          border-radius: 4px;
        }

        .tips-list .tip-item {
          padding: 0.5rem 0;
          border-left: 3px solid transparent;
          padding-left: 10px;
          transition: all 0.2s ease;
        }

        .tips-list .tip-item:hover {
          border-left-color: #007bff;
          background-color: #f8f9fa;
          border-radius: 4px;
        }

        .result-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          transition: all 0.3s ease;
          border: 2px solid #e9ecef !important;
        }

        .result-card:hover {
          background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .metrics-list .metric-item {
          padding: 0.75rem 0;
          border-bottom: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }

        .metrics-list .metric-item:last-child {
          border-bottom: none;
        }

        .metrics-list .metric-item:hover {
          background-color: #f8f9fa;
          padding-left: 8px;
          border-radius: 4px;
        }

        .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .btn {
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        .btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .strategy-parameters .form-control {
          border-radius: 6px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .strategy-parameters .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          transform: translateY(-1px);
        }

        .strategy-parameters .form-control.is-valid {
          border-color: #28a745;
        }

        .strategy-parameters .form-control.is-invalid {
          border-color: #dc3545;
        }

        .card {
          border-radius: 12px;
          overflow: hidden;
        }

        .card-header {
          font-weight: 600;
          border-bottom: 2px solid rgba(0,0,0,0.1);
        }

        .form-label {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .badge {
          font-weight: 500;
          padding: 0.4em 0.65em;
        }

        .parameter-summary {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          border: 1px solid #dee2e6;
        }

        .parameter-summary:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .preset-dropdown .dropdown-menu {
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 1px solid #e9ecef;
        }

        .preset-dropdown .dropdown-item {
          padding: 8px 16px;
          transition: all 0.2s ease;
        }

        .preset-dropdown .dropdown-item:hover {
          background-color: #007bff;
        }

        .validation-alert {
          border-radius: 8px;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .chart-testing-controls {
          background: linear-gradient(
            135deg,
            rgba(13, 110, 253, 0.05) 0%,
            rgba(13, 110, 253, 0.1) 100%
          );
          border-radius: 12px;
          border: 1px solid rgba(13, 110, 253, 0.2);
        }

        .chart-testing-controls:hover {
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
        }

        .status-card {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 10px;
          border: 1px solid #dee2e6;
          transition: all 0.3s ease;
        }

        .status-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .testing-button {
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .testing-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .testing-button:disabled {
          opacity: 0.6;
          transform: none;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .signal-info-badge {
          font-size: 0.9rem;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .backtesting-container {
            padding: 0 10px;
          }

          .card-body {
            padding: 1rem;
          }

          .strategy-parameters .d-flex {
            flex-direction: column;
          }

          .strategy-parameters .ms-2 {
            margin-left: 0 !important;
            margin-top: 8px;
          }

          .chart-testing-controls .d-flex {
            flex-direction: column;
            gap: 0.5rem;
          }

          .chart-testing-controls .d-flex > * {
            width: 100%;
          }
        }
      `}</style>
    </Content>
  );
};

export default Backtesting;
